import React, { useState, useMemo, useEffect, useLayoutEffect, useRef, createContext, useContext, useCallback } from "react";
import { toast } from "sonner";
import { exportAPI, icpAPI, analysisAPI, articlesAPI, shareAPI } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { PresetFitComparison } from "./preset-fit-comparison";

// Default landing tab per onboarding persona. All 11 tabs remain visible
// to every persona — this only determines which tab opens first.
const PERSONA_DEFAULT_TAB = {
  smb_sales: "hypotheses",
  enterprise_ae: "buyingreality",
  pe_bd_corp_dev: "classification",
  founder_executive: "overview",
};

/**
 * Normalize a payment-stack item (processor / digital wallet / BNPL product)
 * to a renderable string label. The Operator persona sometimes returns plain
 * strings ("Stripe") and sometimes returns objects ({name: "Stripe",
 * confidence: 100, evidence: "..."}). React refuses to render the object
 * form, which crashes the entire Overview tab. This helper coerces either
 * shape to a label, optionally suffixed with confidence.
 *
 * Returns `null` for items that resolve to nothing — caller should filter.
 */
const _extractPayItemLabel = (item) => {
  if (item == null) return null;
  if (typeof item === "string") return item.trim() || null;
  if (typeof item === "object") {
    const name = item.name || item.provider || item.label || item.value || item.title;
    if (!name) return null;
    const conf = item.confidence;
    return typeof conf === "number" ? `${String(name).trim()} (${conf}%)` : String(name).trim();
  }
  return String(item).trim() || null;
};

/**
 * Extract any per-item evidence strings from a payment-stack array so they
 * can be folded into the page-level evidence chip row. Dict-shaped items may
 * carry their own `evidence`; string-shaped items carry none.
 */
const _extractPayItemEvidence = (arr) => {
  if (!Array.isArray(arr)) return [];
  const out = [];
  for (const item of arr) {
    if (item && typeof item === "object" && typeof item.evidence === "string" && item.evidence.trim()) {
      out.push(item.evidence.trim());
    }
  }
  return out;
};

// ── CardRow: coordinates side-by-side Card heights ──
// Each Card registers its natural (header + body) expanded height with the row.
// The row computes the max and provides it back via context. Cards use it as a
// min-height on their container when expanded — so every card in the row renders
// at the same height, with no empty space inside when collapsed.
const CardRowContext = createContext(null);

const CardRow = ({ children, style = {}, className, testId }) => {
  const [heights, setHeights] = useState({});
  const register = useCallback((id, h) => {
    setHeights(prev => (prev[id] === h ? prev : { ...prev, [id]: h }));
  }, []);
  const unregister = useCallback((id) => {
    setHeights(prev => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);
  const rowMaxTotalHeight = useMemo(() => {
    const vals = Object.values(heights);
    return vals.length ? Math.max(...vals) : 0;
  }, [heights]);

  // Stabilize the context value so consumer effects don't see a new
  // reference on every render — otherwise their cleanup unregisters and
  // their body re-registers on each pass, causing an infinite loop.
  const ctxValue = useMemo(
    () => ({ rowMaxTotalHeight, register, unregister }),
    [rowMaxTotalHeight, register, unregister]
  );

  return (
    <CardRowContext.Provider value={ctxValue}>
      <div data-testid={testId} className={className} style={{ alignItems: "start", ...style }}>
        {children}
      </div>
    </CardRowContext.Provider>
  );
};

const ACCENT = "#2563EB";
const BG = "#06080F";
const SURFACE = "#0A0D13";
const SURFACE_2 = "#0D1117";
const BORDER = "#1E2433";
const TEXT_PRIMARY = "#F9FAFB";
const TEXT_MUTED = "#6B7280";
const TEXT_DIM = "#374151";

/* ── primitives ── */
const fitColor = s => s >= 75 ? "#22C55E" : s >= 45 ? "#F59E0B" : "#EF4444";
const confColor = s => s >= 80 ? "#22C55E" : s >= 60 ? "#F59E0B" : "#EF4444";
const FitDot = ({ score, size = 10, stale = false }) => (
  <span data-testid={stale ? "fit-dot-stale" : undefined}
    title={stale ? "Scored against older ICP — re-analyze to update" : undefined}
    style={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center", width: size, height: size, flexShrink: 0 }}>
    <span style={{ width: size, height: size, borderRadius: "50%", background: fitColor(score), boxShadow: `0 0 ${size}px ${fitColor(score)}66`, display: "block" }} />
    {stale && (
      <span style={{
        position: "absolute", right: -3, bottom: -3,
        width: Math.max(7, Math.round(size * 0.55)),
        height: Math.max(7, Math.round(size * 0.55)),
        borderRadius: "50%", background: "#0B0F19",
        border: "1px solid #F59E0B",
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 0 4px rgba(245,158,11,0.55)",
      }}>
        <svg width={Math.max(5, Math.round(size * 0.4))} height={Math.max(5, Math.round(size * 0.4))} viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth={3} strokeLinecap="round">
          <polyline points="23 4 23 10 17 10" />
          <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
        </svg>
      </span>
    )}
  </span>
);

const ConfBar = ({ value, label, color }) => {
  const c = color || confColor(value);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ flex: 1, height: 3, background: BORDER, borderRadius: 2 }}>
        <div style={{ width: `${value}%`, height: "100%", background: c, borderRadius: 2, transition: "width 0.4s" }} />
      </div>
      <span style={{ fontSize: 11, color: c, fontFamily: "'DM Mono', monospace", minWidth: 30, textAlign: "right" }}>{value}%</span>
    </div>
  );
};

const Badge = ({ label, color = ACCENT, bg }) => (
  <span style={{
    fontSize: 10, fontWeight: 600, fontFamily: "'DM Mono', monospace",
    color, background: bg || `${color}18`, border: `1px solid ${color}33`,
    borderRadius: 4, padding: "2px 7px", display: "inline-block",
  }}>{label}</span>
);

const Tag = ({ label }) => (
  <span style={{ fontSize: 11, color: TEXT_MUTED, background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 4, padding: "2px 8px" }}>{label}</span>
);

/* ── Collapsible Card with localStorage persistence ── */
const ChevronIcon = ({ expanded }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 44, height: 44, flexShrink: 0, cursor: "pointer" }}>
    <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke={TEXT_DIM} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"
      style={{ transition: "transform 200ms ease", transform: expanded ? "rotate(90deg)" : "rotate(0deg)" }}>
      <path d="M9 5l7 7-7 7" />
    </svg>
  </div>
);

const Card = ({ title, children, tag, titleAction, storageKey, collapsible = true, style, testId }) => {
  const [expanded, setExpanded] = useState(() => {
    if (!collapsible || !storageKey) return true;
    try { const v = localStorage.getItem(storageKey); return v === null ? true : v === "1"; } catch { return true; }
  });
  const headerRef = useRef(null);
  const bodyRef = useRef(null);
  const [bodyHeight, setBodyHeight] = useState(0);
  const [headerHeight, setHeaderHeight] = useState(0);
  const idRef = useRef(Math.random().toString(36).slice(2));
  const rowCtx = useContext(CardRowContext);
  // Extract stable callbacks so the register/unregister effect isn't
  // re-triggered every time rowMaxTotalHeight changes (which would cause
  // an unregister → re-register oscillation and a render storm).
  const register = rowCtx?.register;
  const unregister = rowCtx?.unregister;

  useEffect(() => {
    if (bodyRef.current) setBodyHeight(bodyRef.current.scrollHeight);
    if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight);
  }, [children, expanded]);

  // Report natural (header + body) height to the row so siblings can be matched.
  useEffect(() => {
    if (!register || !unregister) return;
    const id = idRef.current;
    const total = headerHeight + bodyHeight;
    if (total > 0) register(id, total);
    return () => unregister(id);
  }, [register, unregister, headerHeight, bodyHeight]);

  const toggle = (e) => {
    e.stopPropagation();
    setExpanded(prev => {
      const next = !prev;
      if (storageKey) try { localStorage.setItem(storageKey, next ? "1" : "0"); } catch {}
      return next;
    });
  };

  // When inside a CardRow and expanded, use the row's max total as min-height so
  // every card in the row renders at the same height. When collapsed, min-height
  // drops to 0 so the card shrinks to just the header — no empty space inside.
  const minHeight = (rowCtx && expanded) ? rowCtx.rowMaxTotalHeight : 0;

  return (
    <div data-testid={testId} style={{
      background: SURFACE,
      border: `1px solid ${BORDER}`,
      borderRadius: 10,
      overflow: "hidden",
      marginBottom: 12,
      minHeight,
      transition: "min-height 200ms ease",
      ...(style || {}),
    }}>
      <div ref={headerRef} onClick={collapsible ? toggle : undefined} style={{
        padding: "14px 18px",
        display: "flex", alignItems: "center", gap: 8,
        cursor: collapsible ? "pointer" : "default", userSelect: "none",
      }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif", flex: 1 }}>{title}</span>
        {tag && <Badge label={tag} color={TEXT_DIM} bg="#161B27" />}
        {titleAction}
        {collapsible && <ChevronIcon expanded={expanded} />}
      </div>
      <div ref={bodyRef} style={{
        height: expanded ? bodyHeight : 0,
        overflow: "hidden",
        transition: "height 200ms ease",
        borderTop: expanded ? `1px solid ${BORDER}` : "none",
      }}>
        <div style={{ padding: "16px 18px" }}>{children}</div>
      </div>
    </div>
  );
};

// Strip internal Exa pipeline call names from any evidence label before
// rendering. Pattern matches `exa_<anything>:` or `call<anything>:` (with
// optional surrounding whitespace) at the very start of the label only.
// Users must never see these internal call identifiers.
const stripEvidencePrefix = (raw) => {
  if (raw == null) return "";
  const s = String(raw).trim();
  return s.replace(/^(exa_[^:\s]*|call[^:\s]*)\s*:\s*/i, "").trim();
};

const _URL_RE = /^https?:\/\/\S+$/i;

const EvidenceChip = ({ label }) => {
  const clean = stripEvidencePrefix(label);
  const isUrl = _URL_RE.test(clean);
  const baseStyle = {
    fontSize: 10, color: ACCENT, background: `${ACCENT}14`, border: `1px solid ${ACCENT}33`,
    borderRadius: 4, padding: "2px 8px", cursor: "pointer", fontFamily: "'DM Mono', monospace",
    display: "inline-flex", alignItems: "center", gap: 4, textDecoration: "none",
  };
  const icon = (
    <svg width={9} height={9} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
  );
  if (isUrl) {
    return (
      <a href={clean} target="_blank" rel="noopener noreferrer" style={baseStyle}>
        {icon}
        {clean}
      </a>
    );
  }
  return (
    <button style={baseStyle}>
      {icon}
      {clean}
    </button>
  );
};

const SectionLabel = ({ children }) => (
  <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: "0.1em", color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginBottom: 8, textTransform: "uppercase" }}>{children}</div>
);

/* ── Source Badge (Verified / Inferred) ── */
const SourceBadge = ({ source }) => {
  const verified = source === "fingerprint";
  const styles = verified
    ? { bg: "#052E1633", border: "#14532D", color: "#22C55E", label: "VERIFIED" }
    : { bg: "#451A0333", border: "#78350F", color: "#F59E0B", label: "INFERRED" };
  return (
    <span data-testid={`source-badge-${verified ? "verified" : "inferred"}`} style={{
      fontSize: 8.5, fontWeight: 700, letterSpacing: "0.08em",
      fontFamily: "'DM Mono', monospace",
      color: styles.color, background: styles.bg,
      border: `1px solid ${styles.border}`,
      borderRadius: 3, padding: "1px 5px",
      display: "inline-flex", alignItems: "center", gap: 3,
      lineHeight: 1.1,
    }}>
      <svg width={7} height={7} viewBox="0 0 24 24" fill="currentColor">
        {verified
          ? <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
          : <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />}
      </svg>
      {styles.label}
    </span>
  );
};

/* ── Platform Chip with source badge ── */
const PlatformChip = ({ platform }) => {
  const isMatched = platform.matched_target;
  const baseColor = isMatched ? "#22C55E" : ACCENT;
  return (
    <div data-testid={`platform-chip-${(platform.name || "").replace(/\s+/g, "-").toLowerCase()}`}
      title={(platform.evidence || []).join("\n") || `Detected via ${platform.source}`}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        fontSize: 11, padding: "4px 8px 4px 10px",
        background: `${baseColor}14`, border: `1px solid ${baseColor}33`,
        color: baseColor, borderRadius: 14,
        fontFamily: "'DM Sans', sans-serif", fontWeight: 500,
      }}>
      {isMatched && <span style={{ fontSize: 9 }}>★</span>}
      <span>{platform.name}</span>
      <SourceBadge source={platform.source} />
    </div>
  );
};

const UnknownFlag = ({ label }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 12px", background: "#0D0A00", border: "1px solid #78350F", borderRadius: 7, marginBottom: 8 }}>
    <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth={2}><path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
    <span style={{ fontSize: 11, color: "#F59E0B", fontFamily: "'DM Sans', sans-serif" }}>{label}</span>
  </div>
);

/* Part 20 — Data quality notice for tabs with missing/degraded data */
const DataQualityNotice = ({ notices, tab }) => {
  if (!notices || !notices.length) return null;
  const tabNotices = notices.filter(n => n.tab === tab);
  if (!tabNotices.length) return null;
  return (
    <div data-testid={`quality-notice-${tab}`} style={{ marginBottom: 14 }}>
      {tabNotices.map((n, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "#0D0A0022", border: "1px solid #78350F44", borderRadius: 7, marginBottom: 4 }}>
          <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth={2}><path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
          <span style={{ fontSize: 11, color: "#F59E0B", fontFamily: "'DM Sans', sans-serif" }}>{n.message}</span>
        </div>
      ))}
    </div>
  );
};

/* ── Company Header ── */
const CompanyHeader = ({ company, aid, activeTab, onTabChange, tabs, onReanalyze, onScenarioRerun, onPassClick, onSaveClick, onExportClick, isSaved, isReanalyzing, showReanalyzeModal, setShowReanalyzeModal, onRefresh, isRefreshing }) => {
  const handleCopyShareLink = async () => {
    if (!aid) {
      toast.error("Cannot copy link — analysis id missing");
      return;
    }
    // Request a real public share URL from the backend. The backend returns
    // a stable token so calling this multiple times yields the same link,
    // which is what users expect when they re-share.
    let url;
    try {
      const res = await shareAPI.create(aid);
      url = res?.data?.url;
      if (!url) throw new Error("No share URL returned");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create share link");
      return;
    }
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success("Link copied to clipboard", { duration: 3000 });
    } catch (e) {
      toast.error("Could not copy to clipboard");
    }
  };
  return (
  <div data-testid="results-page-header" style={{
    background: SURFACE, borderBottom: `1px solid ${BORDER}`,
    position: "sticky", top: 56, zIndex: 30,
    flexShrink: 0,
  }}>
    {/* Company identity row */}
    <div style={{ padding: "16px 28px", display: "flex", alignItems: "center", gap: 16, borderBottom: `1px solid ${BORDER}` }}>
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
          <FitDot score={company.fitScore} size={10} />
          <h1 style={{ fontSize: 18, fontWeight: 700, color: TEXT_PRIMARY, letterSpacing: "-0.025em", fontFamily: "'DM Sans', sans-serif" }}>
            {company.name}
          </h1>
          <Badge label={company.region || company.industry} color={ACCENT} />
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          {[company.industry, company.region, `Scanned ${company.lastScan}`].filter(Boolean).map((t, i) => (
            <span key={t} style={{ fontSize: 11, color: TEXT_MUTED, fontFamily: "'DM Mono', monospace", display: "flex", alignItems: "center", gap: 5 }}>
              {i > 0 && <span style={{ color: TEXT_DIM }}>·</span>}{t}
            </span>
          ))}
        </div>
      </div>

      {/* Fit scores */}
      <div style={{ display: "flex", gap: 8 }}>
        {[
          { label: "COMPANY FIT", score: company.fitScore, conf: "High" },
          { label: "PERSONA FIT", score: company.personaFit, conf: "Medium" },
          { label: "COMBINED", score: company.combinedFit, conf: "High" },
        ].map((f, i) => (
          <div key={i} style={{
            background: SURFACE_2, border: `1px solid ${BORDER}`,
            borderRadius: 8, padding: "10px 14px", textAlign: "center", minWidth: 80,
          }}>
            <div style={{ fontSize: 9, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginBottom: 4 }}>{f.label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: fitColor(f.score), fontFamily: "'DM Sans', sans-serif", lineHeight: 1 }}>{f.score}</div>
            <div style={{ fontSize: 9, color: TEXT_DIM, marginTop: 3, fontFamily: "'DM Mono', monospace" }}>CONF: {f.conf.toUpperCase()}</div>
          </div>
        ))}
      </div>

      {/* Action buttons */}
      <div style={{ display: "flex", gap: 8, position: "relative" }}>
        <button
          data-testid="header-copy-link-btn"
          onClick={handleCopyShareLink}
          title="Copy shareable link"
          aria-label="Copy shareable link"
          style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            background: SURFACE_2, color: TEXT_MUTED,
            border: `1px solid ${BORDER}`, borderRadius: 7,
            padding: "8px 10px", cursor: "pointer",
            fontFamily: "'DM Sans', sans-serif", transition: "all 0.15s",
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = "#2D3748"; e.currentTarget.style.color = TEXT_PRIMARY; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.color = TEXT_MUTED; }}
        >
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
        </button>
        <button data-testid="refresh-brief-btn" onClick={onRefresh} disabled={isRefreshing}
          title="Re-map this brief with the latest logic — free, no credits used"
          style={{
            display: "flex", alignItems: "center", gap: 6,
            background: SURFACE_2, color: isRefreshing ? ACCENT : TEXT_MUTED,
            border: `1px solid ${isRefreshing ? ACCENT : BORDER}`,
            borderRadius: 7, padding: "8px 14px",
            fontSize: 12, fontWeight: 600, cursor: isRefreshing ? "wait" : "pointer",
            fontFamily: "'DM Sans', sans-serif", transition: "all 0.15s",
          }}
          onMouseEnter={e => { if (!isRefreshing) { e.currentTarget.style.borderColor = "#2D3748"; e.currentTarget.style.color = TEXT_PRIMARY; } }}
          onMouseLeave={e => { if (!isRefreshing) { e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.color = TEXT_MUTED; } }}
        >
          <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round"
            style={{ animation: isRefreshing ? "spin 1s linear infinite" : "none" }}>
            <path d="M21 12a9 9 0 11-6.219-8.56" /><path d="M21 3v6h-6" />
          </svg>
          {isRefreshing ? "Refreshing..." : "Refresh"}
        </button>
        <button data-testid="reanalyze-btn" onClick={() => setShowReanalyzeModal(true)} disabled={isReanalyzing} style={{
          display: "flex", alignItems: "center", gap: 6,
          background: isReanalyzing ? `${ACCENT}44` : SURFACE_2,
          color: isReanalyzing ? ACCENT : TEXT_MUTED,
          border: `1px solid ${isReanalyzing ? ACCENT : BORDER}`,
          borderRadius: 7, padding: "8px 14px",
          fontSize: 12, fontWeight: 600, cursor: isReanalyzing ? "wait" : "pointer",
          fontFamily: "'DM Sans', sans-serif",
          transition: "all 0.15s",
          animation: isReanalyzing ? "pulse 1.5s infinite" : "none",
        }}>
          <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} style={{ animation: isReanalyzing ? "spin 1s linear infinite" : "none" }}>
            <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {isReanalyzing ? "Re-analyzing..." : "Re-analyze"}
        </button>
        {/* Re-analyze modal dropdown */}
        {showReanalyzeModal && !isReanalyzing && (
          <>
            {/* Backdrop — clicking outside the popover closes it */}
            <div data-testid="reanalyze-modal-backdrop" onClick={() => setShowReanalyzeModal(false)} style={{
              position: "fixed", inset: 0, background: "transparent", zIndex: 100,
            }} />
          <div data-testid="reanalyze-modal" style={{
            position: "absolute", top: "100%", right: 0, marginTop: 6,
            background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 10,
            padding: 4, minWidth: 240, zIndex: 101,
            boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
          }}>
            {/* Close X button */}
            <div style={{ display: "flex", justifyContent: "flex-end", padding: "2px 2px 0" }}>
              <button data-testid="reanalyze-modal-close" onClick={() => setShowReanalyzeModal(false)} aria-label="Close" style={{
                background: "transparent", color: TEXT_DIM, border: "none", cursor: "pointer",
                padding: 4, display: "flex", alignItems: "center", borderRadius: 4,
                transition: "color 0.15s",
              }}
                onMouseEnter={e => { e.currentTarget.style.color = TEXT_PRIMARY; }}
                onMouseLeave={e => { e.currentTarget.style.color = TEXT_DIM; }}
              >
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <button data-testid="fresh-analysis-btn" onClick={() => { setShowReanalyzeModal(false); onReanalyze(); }} style={{
              display: "block", width: "100%", textAlign: "left", background: "transparent",
              border: "none", padding: "10px 14px", borderRadius: 8, cursor: "pointer",
              color: TEXT_PRIMARY, fontSize: 13, fontFamily: "'DM Sans', sans-serif",
            }}
              onMouseEnter={e => e.target.style.background = SURFACE_2}
              onMouseLeave={e => e.target.style.background = "transparent"}
            >
              <div style={{ fontWeight: 600, marginBottom: 3 }}>Fresh Analysis</div>
              <div style={{ fontSize: 11, color: TEXT_MUTED }}>Full 6-call Exa + swarm. All tabs refresh.</div>
            </button>
            <button data-testid="scenario-rerun-btn" onClick={() => { setShowReanalyzeModal(false); onScenarioRerun(); }} style={{
              display: "block", width: "100%", textAlign: "left", background: "transparent",
              border: "none", padding: "10px 14px", borderRadius: 8, cursor: "pointer",
              color: TEXT_PRIMARY, fontSize: 13, fontFamily: "'DM Sans', sans-serif",
            }}
              onMouseEnter={e => e.target.style.background = SURFACE_2}
              onMouseLeave={e => e.target.style.background = "transparent"}
            >
              <div style={{ fontWeight: 600, marginBottom: 3 }}>Scenario Re-run</div>
              <div style={{ fontSize: 11, color: TEXT_MUTED }}>Inject a "what if" scenario and see how it changes the analysis.</div>
            </button>
            <div style={{ borderTop: `1px solid ${BORDER}`, margin: "4px 0" }} />
            <button onClick={() => setShowReanalyzeModal(false)} style={{
              display: "block", width: "100%", textAlign: "center", background: "transparent",
              border: "none", padding: "8px 14px", borderRadius: 8, cursor: "pointer",
              color: TEXT_DIM, fontSize: 12, fontFamily: "'DM Sans', sans-serif",
            }}>Cancel</button>
          </div>
          </>
        )}
        {[
          { label: isSaved ? "Saved" : "Save", icon: "M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z", isSave: true, saved: isSaved },
          { label: "Pass", icon: "M6 18L18 6M6 6l12 12", isPass: true },
          { label: "Export", icon: "M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4", isExport: true, accent: true },
        ].map((b, i) => (
          <button key={i} data-testid={`header-${b.label.toLowerCase()}-btn`}
            onClick={
              b.isPass ? onPassClick :
              b.isSave ? onSaveClick :
              b.isExport ? onExportClick :
              undefined
            }
            style={{
            display: "flex", alignItems: "center", gap: 6,
            background: b.accent ? ACCENT : (b.saved ? "#22C55E14" : SURFACE_2),
            color: b.accent ? "#fff" : (b.saved ? "#22C55E" : TEXT_MUTED),
            border: `1px solid ${b.accent ? ACCENT : (b.saved ? "#22C55E33" : BORDER)}`,
            borderRadius: 7, padding: "8px 14px",
            fontSize: 12, fontWeight: 600, cursor: "pointer",
            fontFamily: "'DM Sans', sans-serif",
            transition: "all 0.15s",
          }}>
            <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}><path d={b.icon} /></svg>
            {b.label}
          </button>
        ))}
      </div>
    </div>

    {/* Hero one-liner — the single bold sentence that immediately tells the
        user why this company matters right now and what approach to take.
        Sits between the sticky identity row and the tab row. Renders only
        when the Approach Advisor produced a valid scenario_type. */}
    {(() => {
      const rec = company?.approachRecommendations;
      const scenarioType = (rec?.scenario_type || "unknown").toLowerCase();
      const headline = rec?.recommended_angle?.headline;
      const timingText = rec?.timing_signal?.text;
      const SCENARIO_META = {
        displacement: { label: "Displacement Play", color: "#EF4444", bg: "#450A0A", border: "#7F1D1D" },
        greenfield:   { label: "Greenfield Opportunity", color: "#22C55E", bg: "#06280F", border: "#1E5128" },
        augmentation: { label: "Augmentation Play", color: "#3B82F6", bg: "#0D1829", border: "#1E3A6E" },
      };
      const meta = SCENARIO_META[scenarioType];
      // Empty state: nothing renders — no banner, no placeholder.
      if (!meta || !headline || !timingText) return null;

      const oneLiner = `${headline} — ${timingText}`;
      const rawUrgency = (rec?.timing_signal?.urgency || "low").toLowerCase();
      const urgency = ["high", "medium", "low"].includes(rawUrgency) ? rawUrgency : "low";
      const URGENCY_META = {
        high:   { label: "High",   color: "#FCA5A5", bg: "#450A0A", border: "#7F1D1D" },
        medium: { label: "Medium", color: "#FCD34D", bg: "#1F1A05", border: "#78350F" },
        low:    { label: "Low",    color: "#9CA3AF", bg: "#161B27", border: BORDER },
      };
      const u = URGENCY_META[urgency];

      return (
        <div data-testid="results-hero-strip" style={{
          background: BG,
          borderBottom: `1px solid ${BORDER}`,
          padding: "10px 28px",
          display: "flex",
          alignItems: "center",
          gap: 14,
          fontFamily: "'DM Sans', sans-serif",
        }}>
          <style>{`
            @media (max-width: 767px) {
              [data-testid="results-hero-badge"] { display: none !important; }
              [data-testid="results-hero-strip"] { padding: 10px 14px !important; gap: 10px !important; }
              [data-testid="results-hero-text"] {
                max-width: 80ch !important;
                font-size: 13px !important;
              }
              [data-testid="results-hero-text"] .hero-clamp {
                -webkit-line-clamp: 1 !important;
                max-width: calc(100vw - 100px) !important;
              }
            }
          `}</style>

          {/* Scenario badge — left */}
          <span
            data-testid="results-hero-badge"
            data-scenario={scenarioType}
            style={{
              background: meta.bg,
              color: meta.color,
              border: `1px solid ${meta.border}`,
              borderRadius: 6,
              padding: "3px 9px",
              fontSize: 11,
              fontWeight: 600,
              fontFamily: "'DM Mono', monospace",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {meta.label}
          </span>

          {/* Hero one-liner — center, bold, truncated */}
          <span
            data-testid="results-hero-text"
            title={oneLiner}
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: 14,
              fontWeight: 600,
              color: TEXT_PRIMARY,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "120ch",
            }}
          >
            <span className="hero-clamp" style={{ display: "inline-block", overflow: "hidden", textOverflow: "ellipsis", verticalAlign: "bottom", maxWidth: "100%" }}>
              {oneLiner}
            </span>
          </span>

          {/* Urgency pill — right */}
          <span
            data-testid="results-hero-urgency"
            data-urgency={urgency}
            style={{
              background: u.bg,
              color: u.color,
              border: `1px solid ${u.border}`,
              borderRadius: 999,
              padding: "3px 10px",
              fontSize: 11,
              fontWeight: 600,
              fontFamily: "'DM Mono', monospace",
              letterSpacing: "0.04em",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {u.label}
          </span>
        </div>
      );
    })()}

    {/* Tabs */}
    <div data-mobile-tabs data-testid="results-page-tabs" style={{ display: "flex", padding: "0 20px", gap: 2, overflowX: "auto" }}>
      {tabs.map(tab => (
        <button
          key={tab.id}
          onClick={() => onTabChange(tab.id)}
          style={{
            padding: "12px 14px",
            background: "none", border: "none",
            borderBottom: `2px solid ${activeTab === tab.id ? ACCENT : "transparent"}`,
            color: activeTab === tab.id ? ACCENT : TEXT_MUTED,
            fontSize: 12, fontWeight: activeTab === tab.id ? 600 : 400,
            cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
            whiteSpace: "nowrap", transition: "all 0.15s",
            display: "flex", alignItems: "center", gap: 6,
          }}
        >
          {tab.label}
          {tab.badge && <Badge label={tab.badge} color={activeTab === tab.id ? ACCENT : TEXT_DIM} bg="transparent" />}
        </button>
      ))}
    </div>
  </div>
  );
};

/* ══════════════════════ TABS ══════════════════════ */

/* 1. OVERVIEW */
const OverviewTab = ({ co, aid }) => {
  const [companyShowAll, setCompanyShowAll] = useState(false);
  const [industryShowAll, setIndustryShowAll] = useState(false);
  return (
  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
  <CardRow style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
    {/* Key facts */}
    <Card title="Company Snapshot" storageKey={`inference_card_state_${aid}_overview_company_snapshot`}>
      {[
        { label: "Legal Name", value: co.legalName },
        { label: "Website", value: co.domain },
        { label: "Industry", value: co.industry },
        { label: "Region", value: co.region },
        { label: "Estimated Revenue", value: co.revenue },
        { label: "Employee Count", value: co.employees },
        { label: "Business Model", value: co.bizModel },
        { label: "Sales Motion", value: co.salesMotion },
      ].map((r, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: i < 7 ? `1px solid ${BORDER}` : "none", gap: 12 }}>
          <span style={{ fontSize: 12, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", whiteSpace: "nowrap" }}>{r.label}</span>
          <span style={{ fontSize: 12, color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif", textAlign: "right" }}>{r.value}</span>
        </div>
      ))}
    </Card>

    {/* Payments stack */}
    <Card title="Payments + BNPL Detection" storageKey={`inference_card_state_${aid}_overview_payments_bnpl`}>
      <SectionLabel>Payment Providers</SectionLabel>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
        {co.payments.providers.map(p => <Tag key={p} label={p} />)}
      </div>
      <SectionLabel>Digital Wallets</SectionLabel>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
        {co.payments.wallets.map(p => <Tag key={p} label={p} />)}
      </div>
      <SectionLabel>Checkout Type</SectionLabel>
      <div style={{ marginBottom: 14 }}><Tag label={co.payments.checkoutType} /></div>
      <SectionLabel>Detection Confidence</SectionLabel>
      <ConfBar value={co.payments.confidence} />

      {/* Detected Platforms (fingerprint + LLM merged) */}
      {co.detectedPlatforms?.platforms?.length > 0 && (
        <>
          <div style={{ height: 1, background: BORDER, margin: "14px 0" }} />
          <SectionLabel>Detected Platforms</SectionLabel>
          <div data-testid="detected-platforms-row" style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
            {co.detectedPlatforms.platforms.map(p => (
              <PlatformChip key={`${p.name}-${p.source}`} platform={p} />
            ))}
          </div>
          {co.detectedPlatforms.fingerprintStatus === "failed" && (
            <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginTop: 4 }}>
              Direct probe blocked — showing inferred matches only
            </div>
          )}
        </>
      )}

      <div style={{ height: 1, background: BORDER, margin: "14px 0" }} />
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: co.bnpl.offered ? "#22C55E" : TEXT_DIM }} />
        <span style={{ fontSize: 13, color: TEXT_PRIMARY, fontWeight: 600 }}>BNPL {co.bnpl.offered ? "Offered" : "Not Detected"}</span>
      </div>
      {co.bnpl.offered && (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
            {co.bnpl.providers.map(p => <Tag key={p} label={p} />)}
          </div>
          <div style={{ fontSize: 11, color: TEXT_MUTED, marginBottom: 8 }}>Positioned at: <strong style={{ color: TEXT_PRIMARY }}>{co.bnpl.positioning}</strong></div>
          <ConfBar value={co.bnpl.confidence} />
        </>
      )}
      <div style={{ marginTop: 12, display: "flex", gap: 6, flexWrap: "wrap" }}>
        {co.payments.evidence.map(e => <EvidenceChip key={e} label={e} />)}
      </div>
    </Card>
  </CardRow>

  <CardRow style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
    {/* Fit breakdown */}
    <Card title="Fit Score Breakdown" tag="EXPLAINABLE" storageKey={`inference_card_state_${aid}_overview_fit_breakdown`}>
      <SectionLabel>Contributing Signals</SectionLabel>
      {co.fitBreakdown.map((s, i) => (
        <div key={i} style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ fontSize: 12, color: TEXT_PRIMARY }}>{s.signal}</span>
            <span style={{ fontSize: 11, color: confColor(s.weight), fontFamily: "'DM Mono', monospace" }}>+{s.weight}</span>
          </div>
          <ConfBar value={s.weight} />
        </div>
      ))}
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 14px", background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 8, marginTop: 4 }}>
        <span style={{ fontSize: 12, color: TEXT_MUTED }}>Override scoring?</span>
        <button style={{ marginLeft: "auto", fontSize: 11, color: ACCENT, background: `${ACCENT}14`, border: `1px solid ${ACCENT}33`, borderRadius: 5, padding: "3px 10px", cursor: "pointer", fontFamily: "'DM Mono', monospace" }}>OVERRIDE</button>
      </div>
    </Card>

    {/* Identity resolution */}
    <Card title="Identity Resolution" storageKey={`inference_card_state_${aid}_overview_identity`}>
      {[
        { label: "Input Name", value: co.name },
        { label: "Input Domain", value: co.domain },
        { label: "Resolved By", value: "System (auto)" },
        { label: "Resolution Notes", value: "Canonical match confirmed via domain registry" },
      ].map((r, i) => (
        <div key={i} style={{ padding: "7px 0", borderBottom: i < 3 ? `1px solid ${BORDER}` : "none" }}>
          <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginBottom: 2 }}>{r.label}</div>
          <div style={{ fontSize: 12, color: TEXT_PRIMARY }}>{r.value}</div>
        </div>
      ))}
      <div style={{ marginTop: 12 }}>
        <SectionLabel>Identity Confidence</SectionLabel>
        <ConfBar value={co.identityConf} />
      </div>
    </Card>
  </CardRow>

  <CardRow style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
    {/* Company News */}
    <Card title="Company News" tag={`${(co.articles || []).filter(a => a.category === 'company' || a.title?.toLowerCase().includes(co.name?.toLowerCase()) || a.snippet?.toLowerCase().includes(co.name?.toLowerCase())).length || 0} articles`} storageKey={`inference_card_state_${aid}_overview_company_news`}>
      {(() => {
        const lcName = (co.name || "").toLowerCase();
        const firstWord = lcName.split(/\s+/)[0];
        const matchesName = (a) =>
          a.title?.toLowerCase().includes(firstWord) ||
          a.snippet?.toLowerCase().includes(firstWord);
        const categorized = (co.articles || []).some(a => a.category);
        const companyArticles = categorized
          ? (co.articles || []).filter(a => a.category === 'company')
          : (co.articles || []).filter(matchesName);
        const allArticles = companyArticles.length > 0 ? companyArticles : (co.articles || []).slice(0, 3);
        const visibleArticles = companyShowAll ? allArticles : allArticles.slice(0, 3);
        const hasMore = allArticles.length > 3;
        if (allArticles.length === 0) {
          return <p style={{ fontSize: 12, color: TEXT_DIM, padding: "12px 0" }}>No company-specific articles found.</p>;
        }
        return (
          <>
            {visibleArticles.map((a, i) => (
              <div key={i} style={{ padding: "10px 0", borderBottom: i < visibleArticles.length - 1 ? `1px solid ${BORDER}` : "none" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY, marginBottom: 4 }}>{a.title}</div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ fontSize: 11, color: ACCENT }}>{a.publisher}</span>
                  <span style={{ fontSize: 11, color: TEXT_DIM }}>·</span>
                  <span style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>{a.published || a.date || ""}</span>
                </div>
                {a.snippet && <p style={{ fontSize: 12, color: TEXT_MUTED, marginTop: 4, lineHeight: 1.5 }}>{a.snippet}</p>}
              </div>
            ))}
            {hasMore && (
              <div style={{ margin: "8px -18px -16px", padding: "10px 18px", display: "flex", justifyContent: "flex-end" }}>
                <span data-testid="view-all-company-news-btn" onClick={() => setCompanyShowAll(v => !v)} style={{ fontSize: 10, fontWeight: 600, color: ACCENT, cursor: "pointer", fontFamily: "'DM Mono', monospace", letterSpacing: "0.04em" }}>
                  {companyShowAll ? "SHOW LESS ←" : "VIEW ALL COMPANY NEWS →"}
                </span>
              </div>
            )}
          </>
        );
      })()}
    </Card>

    {/* Industry News */}
    <Card title="Industry News" tag={co.industry || "Market"} storageKey={`inference_card_state_${aid}_overview_industry_news`}>
      {(() => {
        const lcName = (co.name || "").toLowerCase();
        const firstWord = lcName.split(/\s+/)[0];
        const matchesName = (a) =>
          a.title?.toLowerCase().includes(firstWord) ||
          a.snippet?.toLowerCase().includes(firstWord);
        const categorized = (co.articles || []).some(a => a.category);
        const industryArticles = categorized
          ? (co.articles || []).filter(a => a.category === 'industry' || a.category === 'competitors')
          : (co.articles || []).filter(a => !matchesName(a));
        const allArticles = industryArticles;
        const visibleArticles = industryShowAll ? allArticles : allArticles.slice(0, 3);
        const hasMore = allArticles.length > 3;
        if (allArticles.length === 0) {
          return <p style={{ fontSize: 12, color: TEXT_DIM, padding: "12px 0" }}>No industry articles found. Run a fresh analysis to discover market trends.</p>;
        }
        return (
          <>
            {visibleArticles.map((a, i) => (
              <div key={i} style={{ padding: "10px 0", borderBottom: i < visibleArticles.length - 1 ? `1px solid ${BORDER}` : "none" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY, marginBottom: 4 }}>{a.title}</div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ fontSize: 11, color: "#8B5CF6" }}>{a.publisher}</span>
                  <span style={{ fontSize: 11, color: TEXT_DIM }}>·</span>
                  <span style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>{a.published || a.date || ""}</span>
                </div>
                {a.snippet && <p style={{ fontSize: 12, color: TEXT_MUTED, marginTop: 4, lineHeight: 1.5 }}>{a.snippet}</p>}
              </div>
            ))}
            {hasMore && (
              <div style={{ margin: "8px -18px -16px", padding: "10px 18px", display: "flex", justifyContent: "flex-end" }}>
                <span data-testid="view-all-industry-news-btn" onClick={() => setIndustryShowAll(v => !v)} style={{ fontSize: 10, fontWeight: 600, color: ACCENT, cursor: "pointer", fontFamily: "'DM Mono', monospace", letterSpacing: "0.04em" }}>
                  {industryShowAll ? "SHOW LESS ←" : "VIEW ALL INDUSTRY NEWS →"}
                </span>
              </div>
            )}
          </>
        );
      })()}
    </Card>
  </CardRow>
  </div>
  );
};

/* 2. SIGNALS */
// Category filters. Each `match` list is checked as substring against a
// haystack built from the signal's type, description and source text.
// Keywords are kept narrow so a signal can only match ONE category — this
// prevents the previous bug where Geo and eCommerce showed the same cards
// because both matched broad words like "market" or "commerce".
const SIGNAL_FILTERS = [
  { id: "all", label: "All", match: null },
  { id: "payments", label: "Payments", match: ["payment", "processor", "checkout", "stripe", "adyen", "paypal", "shop pay"] },
  { id: "bnpl", label: "BNPL", match: ["bnpl", "klarna", "afterpay", "affirm", "sezzle", "clearpay", "zip", "installment"] },
  { id: "platform", label: "Platform", match: ["shopify", "magento", "bigcommerce", "salesforce commerce", "woocommerce", "sap commerce", "commercetools", "tech stack", "infrastructure"] },
  { id: "pricing", label: "Pricing", match: ["pricing", "discount", "promo", "price cut", "price increase", "aov"] },
  { id: "ecommerce", label: "eCommerce", match: ["ecommerce", "e-commerce", "d2c", "dtc", "gmv", "conversion rate", "cart"] },
  { id: "geo", label: "Geo", match: ["expansion", "expanding into", "new market", "region", "country launch", "emea", "apac", "latam", "geographic"] },
  { id: "compliance", label: "Compliance", match: ["compliance", "regulatory", "pci", "gdpr", "ccpa", "soc 2", "iso"] },
  { id: "opportunity", label: "Opportunity", match: ["opportunity", "growth", "funding", "hiring"] },
  { id: "risk", label: "Risk", match: ["risk", "missing", "layoff", "decline", "loss"] },
];

const _signalMatchesFilter = (s, filterMatch) => {
  if (!filterMatch) return true;
  // Include description + source so free-text categorisation actually works.
  // Previously only `type + label` was checked, but signals emitted by the
  // mapper carry their content in `description` (from persona `value`) — so
  // every keyword-based filter effectively looked at just the generic type
  // string ("risk", "opportunity", "growth"), never matching real category
  // words. That's why multiple filters used to show identical results.
  const haystack = `${s.type || ""} ${s.description || ""} ${s.source || ""} ${s.label || ""}`.toLowerCase();
  return filterMatch.some(m => haystack.includes(m));
};

const SignalsTab = ({ co, aid }) => {
  const [activeFilter, setActiveFilter] = useState("all");
  const filterCfg = SIGNAL_FILTERS.find(f => f.id === activeFilter) || SIGNAL_FILTERS[0];
  // Hide any signal card that has no readable value AND no label — those
  // are placeholder rows the swarm sometimes emits. Do this once and use
  // the deduped list for BOTH the badge counts and the rendered grid so
  // the counts never lie.
  const nonEmptySignals = (co.signals || []).filter(s => {
    if (!s) return false;
    if (s.is_missing) return true; // Missing-signal card is a valid rendering
    return String(s.value || "").trim() || String(s.label || "").trim() || String(s.description || "").trim();
  });
  const visibleSignals = nonEmptySignals.filter(s => _signalMatchesFilter(s, filterCfg.match));

  // Meaningful signal-card title. Prefer the value (short), then the label,
  // then the source. Never render an em-dash placeholder for populated
  // signals — reserved only for actually-missing signals.
  const titleFor = (s) => {
    if (s.is_missing) return "—";
    const raw = String(s.value || s.label || s.description || "").trim();
    if (!raw) return String(s.source || s.type || "Signal");
    return raw.length > 60 ? raw.slice(0, 57) + "…" : raw;
  };

  return (
  <div>
    {/* Platform Detection signal card — fingerprint + LLM merged */}
    {co.detectedPlatforms?.platforms?.length > 0 && (
      <Card title="Platform Detection" tag={`${co.detectedPlatforms.platforms.length} DETECTED`}
        storageKey={`inference_card_state_${aid}_signals_platforms`}
        titleAction={<span style={{ fontSize: 10, color: confColor(co.detectedPlatforms.detectionConfidence), fontFamily: "'DM Mono', monospace" }}>{co.detectedPlatforms.detectionConfidence}%</span>}>
        <div data-testid="signals-platform-card" style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
          {co.detectedPlatforms.platforms.map(p => <PlatformChip key={`${p.name}-${p.source}`} platform={p} />)}
        </div>
        <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", lineHeight: 1.5 }}>
          <span style={{ color: "#22C55E" }}>VERIFIED</span> = matched via direct domain fingerprint &nbsp;|&nbsp;
          <span style={{ color: "#F59E0B" }}>INFERRED</span> = inferred by AI from page content
          {co.detectedPlatforms.fingerprintStatus === "failed" && (
            <span> &nbsp;|&nbsp; <span style={{ color: "#EF4444" }}>direct probe blocked</span> — inferred only</span>
          )}
        </div>
      </Card>
    )}
    <div style={{ height: 12 }} />
    <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
      {SIGNAL_FILTERS.map(f => {
        const isActive = activeFilter === f.id;
        const count = f.match === null
          ? nonEmptySignals.length
          : nonEmptySignals.filter(s => _signalMatchesFilter(s, f.match)).length;
        return (
          <button
            key={f.id}
            data-testid={`signals-filter-${f.id}`}
            onClick={() => setActiveFilter(f.id)}
            style={{
              fontSize: 11, padding: "5px 12px", borderRadius: 6, cursor: "pointer",
              border: `1px solid ${isActive ? ACCENT : BORDER}`,
              background: isActive ? `${ACCENT}18` : "transparent",
              color: isActive ? ACCENT : TEXT_MUTED, fontFamily: "'DM Sans', sans-serif",
              display: "inline-flex", alignItems: "center", gap: 6,
              transition: "all 0.15s",
            }}
          >
            <span>{f.label}</span>
            <span style={{
              fontSize: 9.5, fontFamily: "'DM Mono', monospace",
              color: isActive ? ACCENT : TEXT_DIM, opacity: 0.85,
            }}>{count}</span>
          </button>
        );
      })}
    </div>
    {visibleSignals.length === 0 ? (
      <div data-testid="signals-empty-state" style={{
        padding: "32px 16px", textAlign: "center", border: `1px dashed ${BORDER}`,
        borderRadius: 8, color: TEXT_MUTED, fontSize: 13, fontFamily: "'DM Sans', sans-serif",
      }}>
        No {filterCfg.label.toLowerCase()} signals detected for this company.
      </div>
    ) : (
    <CardRow style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
      {visibleSignals.map((s, i) => (
        <Card key={i} title={titleFor(s)} tag={s.type.toUpperCase()} storageKey={`inference_card_state_${aid}_signals_${i}`}
          titleAction={s.is_missing
            ? <Badge label="MISSING" color="#F59E0B" />
            : <span style={{ fontSize: 10, color: confColor(s.confidence), fontFamily: "'DM Mono', monospace" }}>{s.confidence}%</span>}
        >
          <div style={{ fontSize: 13, fontWeight: 600, color: s.is_missing ? TEXT_MUTED : TEXT_PRIMARY, marginBottom: 4, fontFamily: "'DM Sans', sans-serif" }}>
            {s.is_missing ? "—" : String(s.value)}
          </div>
          <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Sans', sans-serif", marginBottom: 8 }}>{s.label}</div>
          {!s.is_missing && (
            <>
              <ConfBar value={s.confidence} />
              <div style={{ marginTop: 8, display: "flex", gap: 4, flexWrap: "wrap" }}>
                {s.sources.map(src => <EvidenceChip key={src} label={src} />)}
              </div>
            </>
          )}
          {s.is_missing && (
            <div style={{ fontSize: 10, color: "#F59E0B", fontFamily: "'DM Sans', sans-serif" }}>
              Reason: {s.missing_reason}
            </div>
          )}
        </Card>
      ))}
    </CardRow>
    )}
  </div>
  );
};

/* 3. CLASSIFICATION */
const ClassificationTab = ({ co, aid }) => (
  <CardRow style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
    <Card title="Primary Classification" tag={`CONF: ${co.classification.confidence}%`} storageKey={`inference_card_state_${aid}_classification_primary`}>
      {[
        { label: "Company Type", value: co.classification.company_type },
        { label: "Business Model", value: co.classification.business_model },
        { label: "Product Category", value: co.classification.product_category },
        { label: "Region Inference", value: co.classification.region_inference },
        { label: "Sales Motion", value: co.classification.sales_motion },
      ].map((r, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: i < 4 ? `1px solid ${BORDER}` : "none" }}>
          <span style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>{r.label}</span>
          <span style={{ fontSize: 12, color: TEXT_PRIMARY, fontWeight: 500 }}>{r.value}</span>
        </div>
      ))}
      <div style={{ marginTop: 14 }}>
        <SectionLabel>Classification Confidence</SectionLabel>
        <ConfBar value={co.classification.confidence} />
      </div>
      <div style={{ marginTop: 12, display: "flex", gap: 6 }}>
        {co.classification.evidence.map(e => <EvidenceChip key={e} label={e} />)}
      </div>
    </Card>

    <Card title="Alternate Classifications" storageKey={`inference_card_state_${aid}_classification_alternates`}>
      {co.classification.alternates.length === 0 ? (
        <div data-testid="classification-alternates-empty" style={{ fontSize: 12, color: TEXT_MUTED, padding: "4px 0" }}>
          No alternate classifications surfaced for this company.
        </div>
      ) : co.classification.alternates.map((a, i) => (
        <div key={i} style={{ padding: "12px 0", borderBottom: i < co.classification.alternates.length - 1 ? `1px solid ${BORDER}` : "none" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            <span style={{ fontSize: 13, color: TEXT_PRIMARY, fontWeight: 600 }}>{a.label}</span>
            <span style={{ fontSize: 11, color: confColor(a.confidence), fontFamily: "'DM Mono', monospace" }}>{a.confidence}%</span>
          </div>
          <ConfBar value={a.confidence} />
          {a.reason && <div style={{ fontSize: 11, color: TEXT_MUTED, marginTop: 6 }}>{a.reason}</div>}
        </div>
      ))}
    </Card>

    <Card title="Decision Complexity" tag={`SCORE: ${co.complexity.score}/100`} storageKey={`inference_card_state_${aid}_classification_complexity`}>
      <div style={{ marginBottom: 14 }}>
        <ConfBar value={co.complexity.score} />
      </div>
      <SectionLabel>Complexity Drivers</SectionLabel>
      {co.complexity.drivers.map((d, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#F59E0B", flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: TEXT_PRIMARY }}>{d}</span>
        </div>
      ))}
      <div style={{ marginTop: 12 }}>
        <SectionLabel>Evidence</SectionLabel>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {co.complexity.evidence.map(e => <EvidenceChip key={e} label={e} />)}
        </div>
      </div>
    </Card>
  </CardRow>
);

/* 4. HYPOTHESES */
/* ── Approach Recommendations card (Hypotheses tab) ──
 * Final card in the Hypotheses tab. Renders the swarm's Approach Advisor
 * output: timing signal + recommended angle + opening message + key news
 * to reference + things to avoid. Surfaces friendly fallbacks when the
 * advisor was unable to produce a recommendation (no ICP pain/use cases,
 * no news, or the advisor call timed out / failed).
 */
const ApproachRecommendationsCard = ({ rec, aid, companyName }) => {
  const storageKey = `inference_card_state_${aid}_hypotheses_approach`;
  const navToICP = () => {
    try { sessionStorage.setItem('inference_settings_initial_section', 'icp'); } catch (e) { /* ignore */ }
    if (typeof window !== "undefined") {
      const u = new URL(window.location.href);
      u.searchParams.set("nav", "settings");
      window.location.href = u.toString();
    }
  };

  // No payload at all → advisor was never reached (failure / timeout case).
  if (!rec) {
    return (
      <Card title="Approach Recommendations" storageKey={storageKey}>
        <div data-testid="approach-rec-fallback" style={{ fontSize: 13, color: TEXT_MUTED, lineHeight: 1.55 }}>
          Approach recommendations unavailable for this analysis. Re-analyze to try again.
        </div>
      </Card>
    );
  }

  // Sentinel from the advisor when the inputs were insufficient.
  if (rec._unavailable) {
    if (rec.reason === "icp_pain_use_missing") {
      return (
        <Card title="Approach Recommendations" storageKey={storageKey}>
          <div data-testid="approach-rec-icp-empty" style={{ fontSize: 13, color: TEXT_MUTED, lineHeight: 1.55, marginBottom: 12 }}>
            Add Pain Points and Use Cases to your ICP profile to get personalized approach recommendations.
          </div>
          <button
            data-testid="approach-rec-setup-icp"
            onClick={navToICP}
            style={{
              background: ACCENT, color: "#fff", border: "none",
              borderRadius: 8, padding: "8px 14px", fontSize: 12.5, fontWeight: 600,
              cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
            }}
          >Set up ICP</button>
        </Card>
      );
    }
    return (
      <Card title="Approach Recommendations" storageKey={storageKey}>
        <div data-testid="approach-rec-no-news" style={{ fontSize: 13, color: TEXT_MUTED, lineHeight: 1.55 }}>
          Not enough recent news to generate approach recommendations for this company.
        </div>
      </Card>
    );
  }

  const urgency = (rec.timing_signal?.urgency || "low").toLowerCase();
  const urgencyColor = urgency === "high" ? "#22C55E" : urgency === "medium" ? "#F59E0B" : "#6B7280";
  const urgencyBg = urgency === "high" ? "#06280F" : urgency === "medium" ? "#1F1A05" : "#161B27";
  const urgencyBorder = urgency === "high" ? "#1E5128" : urgency === "medium" ? "#78350F" : BORDER;

  // Scenario classification — mirrors the Approach Advisor's `scenario_type`.
  const scenarioType = (rec.scenario_type || "unknown").toLowerCase();
  const scenarioMeta = {
    displacement: { label: "Displacement Play", color: "#EF4444", bg: "#450A0A", border: "#7F1D1D" },
    greenfield: { label: "Greenfield Opportunity", color: "#22C55E", bg: "#06280F", border: "#1E5128" },
    augmentation: { label: "Augmentation Play", color: "#3B82F6", bg: "#0D1829", border: "#1E3A6E" },
  }[scenarioType];
  const cd = (scenarioType === "displacement" && rec.competitive_displacement && typeof rec.competitive_displacement === "object")
    ? rec.competitive_displacement
    : null;
  const cdWeaknesses = Array.isArray(cd?.known_weaknesses) ? cd.known_weaknesses : [];

  const newsItems = Array.isArray(rec.key_news_to_reference) ? rec.key_news_to_reference : [];
  const avoidItems = Array.isArray(rec.avoid) ? rec.avoid : [];

  // Copy-as-outreach-email is only offered when every section the email
  // template references is populated. Otherwise the resulting copy would have
  // [missing] placeholders inside it.
  const fullyPopulated = !!(
    rec.timing_signal && rec.timing_signal.text &&
    rec.recommended_angle && rec.recommended_angle.headline &&
    rec.opening_message && rec.opening_message.suggested_opener &&
    newsItems.length > 0 && newsItems[0].title &&
    avoidItems.length > 0
  );

  const handleCopyEmail = async () => {
    const topNews = newsItems[0] || {};
    const subject = `${companyName} — ${rec.recommended_angle.headline}`;
    // Per scenario, append a tailored line after the timing signal so the
    // outreach copy reflects the strategic frame the advisor produced.
    let scenarioLine = "";
    if (scenarioType === "displacement" && cd?.displacement_angle) {
      const dq = cd.discovery_question ? `\n\n${cd.discovery_question}` : "";
      scenarioLine = `\n\n${cd.displacement_angle}${dq}`;
    } else if (scenarioType === "greenfield" && rec.recommended_angle?.rationale) {
      scenarioLine = `\n\n${rec.recommended_angle.rationale}`;
    } else if (scenarioType === "augmentation" && rec.recommended_angle?.rationale) {
      scenarioLine = `\n\n${rec.recommended_angle.rationale}`;
    }
    const body = [
      rec.opening_message.suggested_opener,
      "",
      `${rec.timing_signal.text}${scenarioLine}`,
      "",
      `${topNews.title}${topNews.how_to_use ? ` — ${topNews.how_to_use}` : ""}`,
      "",
      "Looking forward to connecting.",
    ].join("\n");
    const text = `Subject: ${subject}\n\n${body}`;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success("Outreach email copied to clipboard", { duration: 3000 });
    } catch (e) {
      toast.error("Could not copy to clipboard");
    }
  };

  // Scenario badge rendered into the Card header via the `tag` slot. Custom
  // colored chip — overrides the default Card tag styling using a small
  // wrapper that the existing Badge component does not support natively.
  const scenarioBadge = scenarioMeta ? (
    <span data-testid={`approach-rec-scenario-${scenarioType}`} style={{
      fontSize: 9.5, fontWeight: 700, fontFamily: "'DM Mono', monospace",
      color: scenarioMeta.color, background: scenarioMeta.bg,
      border: `1px solid ${scenarioMeta.border}`, borderRadius: 4,
      padding: "2px 7px", letterSpacing: "0.04em", textTransform: "uppercase",
    }}>{scenarioMeta.label}</span>
  ) : null;

  return (
    <Card title="Approach Recommendations" storageKey={storageKey} titleAction={scenarioBadge}>
      <div data-testid="approach-recommendations-card" data-scenario={scenarioType} style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        {/* Section 1 — Timing */}
        {rec.timing_signal && (
          <div data-testid="approach-rec-timing">
            <SectionLabel>Timing</SectionLabel>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginTop: 6 }}>
              <span style={{
                fontSize: 9.5, fontWeight: 700, fontFamily: "'DM Mono', monospace",
                color: urgencyColor, background: urgencyBg,
                border: `1px solid ${urgencyBorder}`, borderRadius: 4,
                padding: "2.5px 7px", letterSpacing: "0.05em",
                flexShrink: 0, whiteSpace: "nowrap",
              }}>{urgency.toUpperCase()}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY, lineHeight: 1.5 }}>
                  {rec.timing_signal.text}
                </div>
                {rec.timing_signal.reason && (
                  <div style={{ fontSize: 12, color: TEXT_MUTED, marginTop: 4, lineHeight: 1.5 }}>
                    {rec.timing_signal.reason}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Section 2 — Recommended Angle */}
        {rec.recommended_angle && (
          <div data-testid="approach-rec-angle">
            <SectionLabel>Recommended Angle</SectionLabel>
            <div style={{ marginTop: 6 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: TEXT_PRIMARY, lineHeight: 1.45, marginBottom: 6 }}>
                {rec.recommended_angle.headline}
              </div>
              {rec.recommended_angle.rationale && (
                <div style={{ fontSize: 12, color: TEXT_MUTED, lineHeight: 1.55, marginBottom: 8 }}>
                  {rec.recommended_angle.rationale}
                </div>
              )}
              {rec.recommended_angle.news_trigger && (
                <EvidenceChip label={rec.recommended_angle.news_trigger} />
              )}
            </div>
          </div>
        )}

        {/* Section 2.5 — Competitive Displacement (only when scenario_type === "displacement") */}
        {cd && (cd.detected_competitor || cd.displacement_angle || cdWeaknesses.length > 0) && (
          <div data-testid="approach-rec-displacement">
            <SectionLabel>Competitive Displacement</SectionLabel>
            <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 10 }}>
              {cd.detected_competitor && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 11, color: TEXT_MUTED }}>Detected competitor:</span>
                  <span data-testid="approach-rec-displacement-competitor" style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    fontSize: 12, fontWeight: 600, color: TEXT_PRIMARY,
                    background: SURFACE_2, border: `1px solid ${BORDER}`,
                    borderRadius: 6, padding: "3px 9px",
                  }}>
                    {cd.detected_competitor}
                    {cd.source && (
                      <span style={{
                        fontSize: 9, fontWeight: 700, fontFamily: "'DM Mono', monospace",
                        color: cd.source === "verified" ? "#22C55E" : "#F59E0B",
                        letterSpacing: "0.04em", textTransform: "uppercase",
                      }}>{cd.source}</span>
                    )}
                  </span>
                </div>
              )}
              {cdWeaknesses.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, color: TEXT_MUTED, marginBottom: 5 }}>Known weaknesses</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                    {cdWeaknesses.slice(0, 3).map((w, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                        <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth={2} style={{ marginTop: 3, flexShrink: 0 }}>
                          <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <span style={{ fontSize: 12, color: TEXT_PRIMARY, lineHeight: 1.5 }}>{w}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {cd.displacement_angle && (
                <div style={{ fontSize: 13, fontWeight: 700, color: TEXT_PRIMARY, lineHeight: 1.5 }}>
                  {cd.displacement_angle}
                </div>
              )}
              {cd.discovery_question && (
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={ACCENT} strokeWidth={2.2} strokeLinecap="round" style={{ marginTop: 3, flexShrink: 0 }}>
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3M12 17h.01" />
                  </svg>
                  <span style={{ fontSize: 12.5, fontStyle: "italic", color: TEXT_PRIMARY, lineHeight: 1.5 }}>
                    {cd.discovery_question}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Section 3 — Suggested Opening */}
        {rec.opening_message && (
          <div data-testid="approach-rec-opening">
            <SectionLabel>Suggested Opening</SectionLabel>
            <div style={{
              marginTop: 6,
              borderLeft: `3px solid ${ACCENT}`,
              padding: "10px 14px",
              background: "#0D1829",
              borderRadius: "0 8px 8px 0",
              fontSize: 13, fontStyle: "italic", color: TEXT_PRIMARY, lineHeight: 1.55,
            }}>
              "{rec.opening_message.suggested_opener}"
            </div>
            {rec.opening_message.context && (
              <div style={{ fontSize: 11.5, color: TEXT_MUTED, marginTop: 6, lineHeight: 1.5 }}>
                {rec.opening_message.context}
              </div>
            )}
          </div>
        )}

        {/* Section 4 — News to Reference in the Meeting */}
        {newsItems.length > 0 && (
          <div data-testid="approach-rec-news">
            <SectionLabel>News to Reference in the Meeting</SectionLabel>
            <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 8 }}>
              {newsItems.slice(0, 3).map((n, i) => (
                <div key={i} style={{
                  border: `1px solid ${BORDER}`, borderRadius: 8,
                  padding: "10px 12px", background: "#0F141C",
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: TEXT_PRIMARY, lineHeight: 1.4, flex: 1 }}>
                      {n.title}
                    </div>
                    {n.date && (
                      <div style={{ fontSize: 10.5, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", flexShrink: 0 }}>
                        {n.date}
                      </div>
                    )}
                  </div>
                  {n.how_to_use && (
                    <div style={{ fontSize: 11.5, color: TEXT_MUTED, fontStyle: "italic", marginTop: 6, lineHeight: 1.5 }}>
                      {n.how_to_use}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Section 5 — What to Avoid */}
        {avoidItems.length > 0 && (
          <div data-testid="approach-rec-avoid">
            <SectionLabel>What to Avoid</SectionLabel>
            <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 6 }}>
              {avoidItems.slice(0, 3).map((a, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth={2} style={{ marginTop: 2, flexShrink: 0 }}>
                    <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span style={{ fontSize: 12, color: TEXT_PRIMARY, lineHeight: 1.5 }}>{a}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Copy as Outreach Email — only when all 5 sections are populated */}
        {fullyPopulated && (
          <div style={{ display: "flex", justifyContent: "flex-start", paddingTop: 4 }}>
            <button
              data-testid="approach-rec-copy-email"
              onClick={handleCopyEmail}
              style={{
                background: "transparent", color: TEXT_PRIMARY,
                border: `1px solid ${BORDER}`, borderRadius: 8,
                padding: "7px 14px", fontSize: 12, fontWeight: 600,
                cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                display: "flex", alignItems: "center", gap: 7,
                transition: "border-color 0.15s, background 0.15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "#2D3748"; e.currentTarget.style.background = "#161B27"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.background = "transparent"; }}
            >
              <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
              </svg>
              Copy as Outreach Email
            </button>
          </div>
        )}
      </div>
    </Card>
  );
};

/* Talking Points — generated by the Approach Advisor.
   Each point is a SPECIFIC statement the user should raise in the meeting
   (not a question to ask), backed by a real signal from the analysis.
   Card is only rendered when the advisor returned talking_points (≥1). */
const TalkingPointsCard = ({ rec, aid }) => {
  const points = Array.isArray(rec?.talking_points) ? rec.talking_points.filter(
    p => p && (typeof p === "object") && (p.point || "").toString().trim()
  ) : [];
  if (points.length === 0) return null;
  const copyTalkingPoint = async (text) => {
    const value = (text || "").toString().trim();
    if (!value) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
      } else {
        const ta = document.createElement("textarea");
        ta.value = value;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success("Talking point copied", { duration: 3000 });
    } catch (e) {
      toast.error("Could not copy to clipboard");
    }
  };
  return (
    <Card title="Talking Points" tag={`${points.length} POINTS`} storageKey={`inference_card_state_${aid}_hypotheses_talking_points`}>
      <div data-testid="talking-points-list" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {points.map((p, i) => (
          <div
            key={i}
            data-testid={`talking-point-${i}`}
            style={{
              display: "flex", gap: 12,
              padding: "12px 14px",
              background: SURFACE_2 || "#161b27",
              border: `1px solid ${BORDER}`,
              borderRadius: 8,
            }}
          >
            <div style={{
              flexShrink: 0, width: 24, height: 24,
              borderRadius: "50%", background: ACCENT, color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 11, fontWeight: 700, fontFamily: "'DM Mono', monospace",
            }}>{i + 1}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                <div style={{
                  flex: 1, minWidth: 0,
                  fontSize: 14, fontWeight: 700, color: TEXT_PRIMARY,
                  lineHeight: 1.45, fontFamily: "'DM Sans', sans-serif",
                }}>
                  {p.point}
                </div>
                <button
                  type="button"
                  data-testid={`talking-point-${i}-copy`}
                  onClick={() => copyTalkingPoint(p.point)}
                  title="Copy talking point"
                  aria-label="Copy talking point"
                  style={{
                    flexShrink: 0,
                    background: "transparent",
                    border: `1px solid ${BORDER}`,
                    borderRadius: 6,
                    padding: "4px 6px",
                    color: TEXT_DIM,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "color 120ms ease, border-color 120ms ease",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = TEXT_PRIMARY; e.currentTarget.style.borderColor = ACCENT; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = TEXT_DIM; e.currentTarget.style.borderColor = BORDER; }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                </button>
              </div>
              {p.rationale && (
                <div style={{
                  fontSize: 12.5, color: TEXT_DIM, marginTop: 6,
                  lineHeight: 1.5, fontFamily: "'DM Sans', sans-serif",
                }}>
                  {p.rationale}
                </div>
              )}
              {p.evidence && (
                <div style={{ marginTop: 9 }}>
                  <span style={{
                    display: "inline-block",
                    fontSize: 10, fontWeight: 600,
                    color: TEXT_DIM, background: "transparent",
                    border: `1px solid ${BORDER}`, borderRadius: 4,
                    padding: "3px 8px", fontFamily: "'DM Mono', monospace",
                    letterSpacing: "0.04em",
                  }}>{stripEvidencePrefix(p.evidence)}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

const HypothesesTab = ({ co, aid }) => (
  <div>
    {/* Primary */}
    <Card title="Primary Hypothesis" tag={`CONF: ${co.hypotheses.primary.confidence}%`} storageKey={`inference_card_state_${aid}_hypotheses_primary`}>
      <div style={{ fontSize: 15, fontWeight: 600, color: TEXT_PRIMARY, lineHeight: 1.5, marginBottom: 14, fontFamily: "'DM Sans', sans-serif" }}>
        {co.hypotheses.primary.text}
      </div>
      <ConfBar value={co.hypotheses.primary.confidence} />
      <div style={{ marginTop: 14 }}>
        <SectionLabel>Evidence</SectionLabel>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {co.hypotheses.primary.evidence.map(e => <EvidenceChip key={e} label={e} />)}
        </div>
      </div>
    </Card>

    {/* Alternates */}
    <Card title="Alternate Hypotheses" storageKey={`inference_card_state_${aid}_hypotheses_alternates`}>
      <div style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginBottom: 12, lineHeight: 1.5 }}>
        % = analyst confidence this alternate hypothesis is the more accurate read of the target company.
      </div>
      {co.hypotheses.alternates.map((h, i) => (
        <div key={i} style={{ padding: "14px 0", borderBottom: i < co.hypotheses.alternates.length - 1 ? `1px solid ${BORDER}` : "none" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY, flex: 1, paddingRight: 16, lineHeight: 1.4 }}>{h.text}</span>
            <span style={{ fontSize: 11, color: confColor(h.confidence), fontFamily: "'DM Mono', monospace", flexShrink: 0 }}>{h.confidence}%</span>
          </div>
          <ConfBar value={h.confidence} />
        </div>
      ))}
    </Card>

    {/* Unknowns */}
    <Card title="Explicit Unknowns" storageKey={`inference_card_state_${aid}_hypotheses_unknowns`}>
      {co.hypotheses.unknowns.map((u, i) => <UnknownFlag key={i} label={u} />)}
    </Card>

    {/* Talking Points — sits between Discovery Questions and Approach Recommendations.
        Renders only when the Approach Advisor produced talking_points. */}
    <TalkingPointsCard rec={co.approachRecommendations} aid={aid} />

    {/* Approach Recommendations — synthesis-time advisor that maps recent news + buying intel + the user's ICP pain points to a specific outreach plan. */}
    <ApproachRecommendationsCard rec={co.approachRecommendations} aid={aid} companyName={co.companyName || co.identity?.legalName || co.name || "your company"} />
  </div>
);

/* 5. BUYING REALITY */
/* Key Contacts — populated from Exa Call 7 (people-search). Renders only
   on the Buying Reality tab when contacts are present, OR shows the ICP
   nudge if the user has no target_personas configured. Empty contacts +
   personas defined → card is hidden entirely. */
const KeyContactsCard = ({ contacts, icpHasPersonas, aid }) => {
  // Client-side belt-and-braces for legacy analyses saved before the
  // backend-wide prefix sanitizer landed. Strips "Exa Call 7:",
  // "LinkedIn:", "Source: ...", etc. from the visible name. New analyses
  // arrive already clean.
  const _stripPrefix = (s) => {
    if (!s || typeof s !== "string") return s || "";
    return s
      .replace(/^\s*(?:exa[^:]*|call\s*\d+[^:]*|linkedin|source|per\s+exa)\s*[:\-]\s*/i, "")
      .replace(/\s*\((?:exa[^)]*|linkedin[^)]*)\)\s*/gi, " ")
      .replace(/\s{2,}/g, " ")
      .trim();
  };
  const list = Array.isArray(contacts)
    ? contacts
        .map(c => c ? { ...c, name: _stripPrefix(c.name), title: _stripPrefix(c.title) } : c)
        .filter(c => c && (c.name || "").toString().trim())
    : [];
  const navToICP = () => {
    try { sessionStorage.setItem('inference_settings_initial_section', 'icp'); } catch (e) { /* ignore */ }
    if (typeof window !== "undefined") {
      const u = new URL(window.location.href);
      u.searchParams.set("nav", "settings");
      window.location.href = u.toString();
    }
  };

  // ICP nudge state — render when no personas AND no contacts.
  if (list.length === 0) {
    if (!icpHasPersonas) {
      return (
        <Card title="Key Contacts" storageKey={`inference_card_state_${aid}_buying_key_contacts`}>
          <div data-testid="key-contacts-icp-empty" style={{ fontSize: 13, color: TEXT_MUTED, lineHeight: 1.55, marginBottom: 12 }}>
            Add Target Personas to your ICP profile to discover key contacts at this company.
          </div>
          <button
            data-testid="key-contacts-setup-icp"
            onClick={navToICP}
            style={{
              background: ACCENT, color: "#fff", border: "none",
              borderRadius: 8, padding: "8px 14px", fontSize: 12.5, fontWeight: 600,
              cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
            }}
          >Set up ICP</button>
        </Card>
      );
    }
    // ICP has personas but no contacts → hide card entirely.
    return null;
  }

  const isLinkedIn = (url) => {
    if (!url || typeof url !== "string") return false;
    try {
      const u = url.trim().toLowerCase();
      return u.startsWith("https://www.linkedin.com") || u.startsWith("https://linkedin.com")
        || u.startsWith("http://www.linkedin.com") || u.startsWith("http://linkedin.com");
    } catch (e) { return false; }
  };

  return (
    <Card title="Key Contacts" tag={`${list.length} FOUND`} storageKey={`inference_card_state_${aid}_buying_key_contacts`}>
      <div data-testid="key-contacts-list" style={{ display: "flex", flexDirection: "column" }}>
        {list.map((c, i) => {
          const conf = (c.confidence || "").toString().toLowerCase();
          const linkOk = isLinkedIn(c.linkedin_url);
          return (
            <div
              key={i}
              data-testid={`key-contact-${i}`}
              style={{
                display: "flex", alignItems: "flex-start", gap: 12,
                padding: "12px 0",
                borderBottom: i < list.length - 1 ? `1px solid ${BORDER}` : "none",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif" }}>
                    {c.name}
                  </span>
                  {c.title && (
                    <span style={{ fontSize: 12, color: TEXT_MUTED }}>
                      {c.title}
                    </span>
                  )}
                  {conf === "high" && <Badge label="CONFIRMED" color="#22C55E" />}
                  {conf === "medium" && <Badge label="LIKELY" color="#F59E0B" />}
                </div>
                {c.profile_context && (
                  <div style={{
                    fontSize: 11.5, color: TEXT_DIM, marginTop: 5,
                    fontStyle: "italic", lineHeight: 1.5,
                  }}>
                    {c.profile_context}
                  </div>
                )}
              </div>
              {linkOk && (
                <a
                  href={c.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`key-contact-${i}-linkedin`}
                  style={{
                    flexShrink: 0,
                    background: "transparent",
                    border: `1px solid ${BORDER}`,
                    color: TEXT_DIM,
                    borderRadius: 6,
                    padding: "5px 10px",
                    fontSize: 11,
                    fontWeight: 600,
                    fontFamily: "'DM Sans', sans-serif",
                    textDecoration: "none",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    transition: "color 120ms ease, border-color 120ms ease",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = TEXT_PRIMARY; e.currentTarget.style.borderColor = ACCENT; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = TEXT_DIM; e.currentTarget.style.borderColor = BORDER; }}
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                  </svg>
                  View on LinkedIn
                </a>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
};

const BuyingRealityTab = ({ co, aid }) => {
  const roleColor = (r) => ({ Champion: "#22C55E", "Economic Buyer": "#F59E0B", "Technical Buyer": ACCENT, Procurement: "#8B5CF6" }[r] || TEXT_MUTED);
  return (
    <CardRow style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
      <Card title="Buyer Departments" storageKey={`inference_card_state_${aid}_buying_departments`}>
        {co.buyingReality.departments.map((d, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: i < co.buyingReality.departments.length - 1 ? `1px solid ${BORDER}` : "none" }}>
            <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={TEXT_MUTED} strokeWidth={2}><path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
            <span style={{ fontSize: 13, color: TEXT_PRIMARY }}>{d}</span>
          </div>
        ))}
      </Card>

      <Card title="Decision Roles" storageKey={`inference_card_state_${aid}_buying_roles`}>
        {co.buyingReality.roles.map((r, i) => (
          <div key={i} style={{ padding: "10px 0", borderBottom: i < co.buyingReality.roles.length - 1 ? `1px solid ${BORDER}` : "none" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <Badge label={r.role.toUpperCase()} color={roleColor(r.role)} />
              {r.identified ? <Badge label="IDENTIFIED" color="#22C55E" /> : <Badge label="NOT YET IDENTIFIED" color={TEXT_DIM} bg="#161B27" />}
            </div>
            {r.name && <div style={{ fontSize: 12, color: TEXT_PRIMARY, marginTop: 6 }}>{r.name}</div>}
            {r.note && <div style={{ fontSize: 11, color: TEXT_MUTED, marginTop: 3 }}>{r.note}</div>}
          </div>
        ))}
      </Card>

      <KeyContactsCard contacts={co.keyContacts} icpHasPersonas={co.icpHasPersonas} aid={aid} />

      <Card title="Buying Triggers" storageKey={`inference_card_state_${aid}_buying_triggers`}>
        {co.buyingReality.triggers.map((t, i) => (
          <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 10 }}>
            <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth={2} style={{ marginTop: 1, flexShrink: 0 }}><path d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <span style={{ fontSize: 12, color: TEXT_PRIMARY, lineHeight: 1.5 }}>{t}</span>
          </div>
        ))}
      </Card>

      <Card title="Objections + Risks" storageKey={`inference_card_state_${aid}_buying_objections`}>
        {co.buyingReality.objections.map((o, i) => (
          <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 10 }}>
            <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth={2} style={{ marginTop: 1, flexShrink: 0 }}><path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
            <span style={{ fontSize: 12, color: TEXT_PRIMARY, lineHeight: 1.5 }}>{o}</span>
          </div>
        ))}
      </Card>
    </CardRow>
  );
};

/* 6. MEDDICC */
const MeddiccTab = ({ co, aid }) => {
  // Backend (post-Iter 47) produces statuses "filled" / "gap" / "unknown" with
  // an optional legacy "known" / "partial" / "assumed" from older docs. The
  // colour map below covers BOTH families so the M-E-D-D-I-C-C letter strip
  // and per-dimension card chrome reflect real status rather than always
  // showing as grey (the bug the user described as "letter highlights stale").
  const statusColor = (s) => ({
    filled: "#22C55E",    // green — data present, confident
    known: "#22C55E",     // legacy alias for filled
    gap: "#F59E0B",       // amber — gap with a discovery question
    partial: "#F59E0B",   // legacy
    assumed: "#F59E0B",   // legacy
    unknown: "#EF4444",   // red — no data
  }[(s || "").toLowerCase()] || TEXT_DIM);
  return (
    <div>
      {/* Summary strip */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {co.meddicc.fields.map((f, i) => (
          <div key={i} style={{
            background: SURFACE_2, border: `1px solid ${statusColor(f.status)}44`,
            borderRadius: 8, padding: "8px 12px", textAlign: "center", minWidth: 80,
          }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: statusColor(f.status), fontFamily: "'DM Mono', monospace" }}>{f.key}</div>
            <div style={{ fontSize: 9, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginTop: 2 }}>{f.status.toUpperCase()}</div>
          </div>
        ))}
      </div>

      {/* Fields detail */}
      {co.meddicc.fields.map((f, i) => (
        <Card key={i} title={f.fullLabel} tag={f.status.toUpperCase()} storageKey={`inference_card_state_${aid}_meddicc_${f.key}`} titleAction={
          <span style={{ fontSize: 11, color: statusColor(f.status), fontFamily: "'DM Mono', monospace" }}>{f.confidence}%</span>
        }>
          {/*
            * Show the persona-produced value whenever it exists. Previously
            * we only rendered it for status="filled"/"known"/"partial"/
            * "assumed" — but every persona except Champion typically returns
            * status="gap" WITH a real value (e.g. "CFO or VP Finance owns
            * payment infrastructure decisions"). Gating on status hid 6 of 7
            * MEDDICC dimensions in production. Now: if value present → show
            * it + confidence bar + evidence + status tag; if empty → fall
            * back to the UnknownFlag with the gap description.
            */}
          {f.value ? (
            <>
              <p style={{ fontSize: 13, color: TEXT_PRIMARY, lineHeight: 1.6, marginBottom: 12 }}>{f.value}</p>
              <ConfBar value={f.confidence} />
              <div style={{ marginTop: 10, display: "flex", gap: 6, flexWrap: "wrap" }}>
                {(f.evidence || []).filter(Boolean).map(e => <EvidenceChip key={e} label={e} />)}
              </div>
            </>
          ) : (
            <UnknownFlag label={f.gap} />
          )}
          {f.nextQuestion && (
            <div style={{ marginTop: 10, padding: "10px 12px", background: `${ACCENT}0F`, border: `1px solid ${ACCENT}22`, borderRadius: 7 }}>
              <div style={{ fontSize: 9, color: ACCENT, fontFamily: "'DM Mono', monospace", marginBottom: 4 }}>RECOMMENDED QUESTION</div>
              <div style={{ fontSize: 12, color: "#93C5FD", fontStyle: "italic" }}>"{f.nextQuestion}"</div>
            </div>
          )}
        </Card>
      ))}
    </div>
  );
};

/* 7. TRAFFIC */
const TREND_META = {
  up: { color: "#22C55E", label: "Trending up", arrow: "M5 15l7-7 7 7" },
  down: { color: "#EF4444", label: "Trending down", arrow: "M19 9l-7 7-7-7" },
  flat: { color: "#F59E0B", label: "Holding flat", arrow: "M5 12h14" },
  unknown: { color: "#6B7280", label: "Direction unknown", arrow: "M12 8v4m0 4h.01" },
};

const TrafficTrendBody = ({ traffic }) => {
  const series = traffic.trend_series || [];
  if (series.length > 0) {
    const max = Math.max(...series.map(t => t.value)) || 1;
    const fmt = v => (v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : String(Math.round(v)));
    return (
      <div data-testid="traffic-trend-series" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {series.map((t, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", minWidth: 60 }}>{t.period}</span>
            <div style={{ flex: 1, height: 14, background: SURFACE_2, borderRadius: 3, overflow: "hidden" }}>
              <div style={{
                width: `${Math.max(4, (t.value / max) * 100)}%`, height: "100%",
                background: `linear-gradient(90deg, ${ACCENT} 0%, ${ACCENT}88 100%)`,
                borderRadius: 3, transition: "width 0.4s",
              }} />
            </div>
            <span style={{ fontSize: 11, color: TEXT_MUTED, fontFamily: "'DM Mono', monospace", minWidth: 70, textAlign: "right" }}>
              {fmt(t.value)}
            </span>
          </div>
        ))}
      </div>
    );
  }

  // No measured series — show the directional read derived from Exa evidence
  // instead of an empty card. Always labelled as an estimate.
  const meta = TREND_META[traffic.trendDirection] || TREND_META.unknown;
  return (
    <div data-testid="traffic-trend-directional">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <span style={{
          width: 30, height: 30, borderRadius: 9, flexShrink: 0,
          background: `${meta.color}18`, border: `1px solid ${meta.color}44`,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke={meta.color} strokeWidth={2.2}><path d={meta.arrow} /></svg>
        </span>
        <div>
          <div data-testid="traffic-trend-direction" style={{ fontSize: 14, fontWeight: 700, color: meta.color }}>{meta.label}</div>
          <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.08em" }}>
            ESTIMATED · NO MEASURED SERIES
          </div>
        </div>
      </div>
      <p data-testid="traffic-trend-reason" style={{ fontSize: 12.5, color: TEXT_MUTED, lineHeight: 1.65 }}>
        {traffic.trendReason
          || (traffic.trendDirection === "unknown"
            ? "No historical traffic series and no directional signal in the sourced evidence. Monthly-visit platforms (SimilarWeb/SEMrush) are not connected."
            : `Direction inferred from the evidence gathered for this company. Confidence ${traffic.confidence}%.`)}
      </p>
      <div style={{ marginTop: 14 }}>
        <SectionLabel>Estimate Confidence</SectionLabel>
        <ConfBar value={traffic.confidence} />
      </div>
    </div>
  );
};

const TrafficTab = ({ co, aid }) => (
  <CardRow style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
    <Card title="Traffic Overview" tag={`CONF: ${co.traffic.confidence}%`} storageKey={`inference_card_state_${aid}_traffic_overview`}>
      {[
        { label: "Est. Monthly Visits", value: co.traffic.visits },
        { label: "MoM Trend", value: co.traffic.trend },
        { label: "Primary Channel", value: co.traffic.primaryChannel },
        { label: "Source", value: co.traffic.source },
      ].map((r, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: i < 3 ? `1px solid ${BORDER}` : "none" }}>
          <span style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>{r.label}</span>
          <span style={{ fontSize: 13, color: TEXT_PRIMARY, fontWeight: 600 }}>{r.value}</span>
        </div>
      ))}
      <div style={{ marginTop: 14 }}>
        <SectionLabel>Data Confidence</SectionLabel>
        <ConfBar value={co.traffic.confidence} />
      </div>
    </Card>

    <Card title="Traffic Trend" tag={co.traffic.estimated ? "ESTIMATED" : "MEASURED"} storageKey={`inference_card_state_${aid}_traffic_trend`}>
      <TrafficTrendBody traffic={co.traffic} />
    </Card>
  </CardRow>
);

/* 8. ARTICLES — Part 17 Summarize + Save */
const ArticlesTab = ({ co, aid }) => {
  const [filter, setFilter] = useState("All");
  const [summaries, setSummaries] = useState({});
  const [loadingSummary, setLoadingSummary] = useState({});
  const [savedArticles, setSavedArticles] = useState({});
  const [showPaywalled, setShowPaywalled] = useState(false);

  // Pre-filter paywalled articles by default. Paywalled articles cannot be
  // summarised by the LLM (the underlying scrape returns only the snippet),
  // so showing them with a disabled Summarize button + later "Failed to
  // generate summary" toast is misleading. Users can opt-in to see them via
  // the toggle below.
  const accessibleArticles = (showPaywalled ? co.articles : co.articles.filter(a => !a.paywall));

  const filtered = filter === "All" ? accessibleArticles :
    accessibleArticles.filter(a => (filter === "Company" && (!a.category || a.category === "company")) ||
      (filter === "Industry" && a.category === "industry") ||
      (filter === "Competitors" && a.category === "competitors"));

  const paywalledCount = co.articles.filter(a => a.paywall).length;

  const handleSummarize = async (article, idx) => {
    if (loadingSummary[idx] || summaries[idx]) return;
    setLoadingSummary(prev => ({ ...prev, [idx]: true }));
    try {
      const res = await articlesAPI.summarize({
        article_url: article.sourceUrl || "",
        article_title: article.title || "",
        company_name: co.name || "",
      });
      if (res.data?.summary) {
        setSummaries(prev => ({ ...prev, [idx]: res.data.summary }));
      } else {
        // No summary returned but no error either — treat as inaccessible and
        // hide so user never sees the failure message.
        setSummaries(prev => ({ ...prev, [idx]: "__inaccessible__" }));
      }
    } catch (err) {
      // Mark with a sentinel so the failure message NEVER reaches the UI —
      // the article was simply inaccessible. Surfaces as a quiet skip.
      setSummaries(prev => ({ ...prev, [idx]: "__inaccessible__" }));
    }
    setLoadingSummary(prev => ({ ...prev, [idx]: false }));
  };

  const handleSave = async (article) => {
    try {
      await articlesAPI.save({
        title: article.title || "", source_url: article.sourceUrl || "",
        publisher: article.publisher || "", published: article.published || "",
        company: co.name || "", snippet: article.snippet || "",
        summary: summaries[co.articles.indexOf(article)] || "",
      });
      setSavedArticles(prev => ({ ...prev, [article.title]: true }));
    } catch (err) { console.error("Save failed:", err); }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        {["All", "Company", "Industry", "Competitors"].map(f => (
          <button key={f} data-testid={`articles-filter-${f.toLowerCase()}`} onClick={() => setFilter(f)} style={{
            fontSize: 11, padding: "5px 12px", borderRadius: 6, cursor: "pointer",
            border: `1px solid ${f === filter ? ACCENT : BORDER}`,
            background: f === filter ? `${ACCENT}18` : "transparent",
            color: f === filter ? ACCENT : TEXT_MUTED, fontFamily: "'DM Sans', sans-serif",
          }}>{f}</button>
        ))}
        {paywalledCount > 0 && (
          <button
            data-testid="articles-paywall-toggle"
            onClick={() => setShowPaywalled(s => !s)}
            style={{
              fontSize: 10, padding: "4px 10px", borderRadius: 6, cursor: "pointer",
              border: `1px solid ${BORDER}`, background: "transparent",
              color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginLeft: "auto",
            }}
            title={showPaywalled ? "Hide paywalled articles that can't be summarised" : "Show paywalled articles (cannot be summarised)"}
          >{showPaywalled ? "HIDE" : "SHOW"} {paywalledCount} PAYWALLED</button>
        )}
      </div>
      {filtered.length === 0 && <div style={{ padding: 24, textAlign: "center", color: TEXT_DIM, fontSize: 13 }}>No articles found.</div>}
      {filtered.map((a, i) => {
        const gIdx = co.articles.indexOf(a);
        return (
          <Card key={i} title={a.title || `Article ${i+1}`} storageKey={`inference_card_state_${aid}_articles_${i}`}
            tag={a.relevance >= 80 ? "HIGH RELEVANCE" : "MEDIUM"}
            titleAction={<div style={{ display: "flex", gap: 4 }}>
              {a.paywall && <Badge label="PAYWALL" color={TEXT_DIM} />}
              {a.category && <Badge label={a.category.toUpperCase()} color={TEXT_DIM} />}
            </div>}>
            <p style={{ fontSize: 12, color: TEXT_MUTED, lineHeight: 1.6, marginBottom: 8 }}>{a.snippet}</p>
            <div style={{ display: "flex", gap: 12, fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginBottom: 10 }}>
              <span>{a.publisher}</span><span>·</span><span>{a.published}</span>
              {a.sourceUrl && <><span>·</span><a href={a.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ color: ACCENT, textDecoration: "none" }}>View source</a></>}
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button data-testid={`summarize-btn-${i}`} onClick={(e) => { e.stopPropagation(); handleSummarize(a, gIdx); }}
                disabled={a.paywall || loadingSummary[gIdx] || !!summaries[gIdx]}
                style={{ background: loadingSummary[gIdx] ? `${ACCENT}44` : `${ACCENT}14`, border: `1px solid ${ACCENT}33`, borderRadius: 7, padding: "6px 12px", fontSize: 11, color: a.paywall ? TEXT_DIM : ACCENT, cursor: a.paywall ? "not-allowed" : "pointer", fontFamily: "'DM Mono', monospace", opacity: a.paywall ? 0.4 : 1 }}
              >{loadingSummary[gIdx] ? "Summarizing..." : summaries[gIdx] ? "Summarized" : "Summarize"}</button>
              <button data-testid={`save-article-btn-${i}`} onClick={(e) => { e.stopPropagation(); handleSave(a); }}
                style={{ background: savedArticles[a.title] ? "#22C55E14" : SURFACE_2, border: `1px solid ${savedArticles[a.title] ? "#22C55E33" : BORDER}`, borderRadius: 7, padding: "6px 12px", fontSize: 11, color: savedArticles[a.title] ? "#22C55E" : TEXT_MUTED, cursor: "pointer", fontFamily: "'DM Mono', monospace" }}
              >{savedArticles[a.title] ? "SAVED" : "SAVE"}</button>
            </div>
            {summaries[gIdx] && summaries[gIdx] !== "__inaccessible__" && (
              <div style={{ marginTop: 12, padding: "12px 14px", background: SURFACE_2, borderRadius: 8, border: `1px solid ${BORDER}` }}>
                <div style={{ fontSize: 10, color: ACCENT, fontFamily: "'DM Mono', monospace", marginBottom: 6, letterSpacing: "0.06em" }}>AI SUMMARY</div>
                <div style={{ fontSize: 12, color: TEXT_PRIMARY, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{summaries[gIdx]}</div>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
};

/* 9. SAVED SUMMARIES */
const SavedSummariesTab = ({ co, aid }) => {
  // Unified saved-summaries pipeline: previously the Save button on the
  // Articles tab wrote to db.articles while this tab read from
  // co.savedSummaries (an analysis-doc field that no code ever populated),
  // so nothing saved via the Articles tab ever showed up here. Now we
  // fetch db.articles directly for the current workspace and filter to
  // the analysed company so the tab shows the real saved list.
  const [remoteSaves, setRemoteSaves] = useState([]);
  useEffect(() => {
    let cancelled = false;
    articlesAPI.list().then(res => {
      if (cancelled) return;
      const items = Array.isArray(res?.data) ? res.data : (res?.data?.articles || []);
      // Filter to the current analysis's company where possible; if the
      // company field is missing on saved records, include them all as a
      // safe fallback so nothing is hidden from the user.
      const targetCo = (co?.name || "").toLowerCase().trim();
      const filtered = items.filter(a => {
        if (!targetCo) return true;
        const c = (a.company || "").toLowerCase().trim();
        return !c || c === targetCo;
      });
      setRemoteSaves(filtered.map(a => ({
        title: a.title || "",
        folder: a.folder || "all",
        savedBy: a.saved_by || "You",
        date: a.created_at ? new Date(a.created_at).toLocaleString() : "",
        content: a.summary || a.snippet || "",
        company: a.company || co?.name || "",
        url: a.source_url || "",
      })));
    }).catch(() => { /* leave list empty on failure */ });
    return () => { cancelled = true; };
  }, [co?.name]);

  // Legacy per-analysis inline saves (co.savedSummaries) are still surfaced
  // if any historical documents have them; the remote list is the source
  // of truth for anything saved via the Articles tab going forward.
  const legacy = (co && co.savedSummaries && co.savedSummaries.length > 0) ? co.savedSummaries.map(s => ({
    title: s.title || "",
    folder: s.folder || "",
    savedBy: s.author || s.saved_by || "You",
    date: s.date || "",
    content: s.content || "",
    company: s.company || co.name,
  })) : [];

  const summaries = [...remoteSaves, ...legacy];
  return (
  <div>
    <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
      {["My Summaries", "Shared (Team)"].map((f, i) => (
        <button key={f} style={{
          fontSize: 11, padding: "5px 14px", borderRadius: 6, cursor: "pointer",
          border: `1px solid ${i === 0 ? ACCENT : BORDER}`,
          background: i === 0 ? `${ACCENT}18` : "transparent",
          color: i === 0 ? ACCENT : TEXT_MUTED, fontFamily: "'DM Sans', sans-serif",
        }}>{f}</button>
      ))}
    </div>
    {summaries.length === 0 && (
      <div data-testid="saved-summaries-empty" style={{
        padding: "32px 16px", textAlign: "center", border: `1px dashed ${BORDER}`,
        borderRadius: 8, color: TEXT_MUTED, fontSize: 13, fontFamily: "'DM Sans', sans-serif",
      }}>
        No saved summaries yet. Use the SAVE button on any article to capture it here.
      </div>
    )}
    {summaries.map((s, i) => (
      <Card key={i} title={s.title} storageKey={`inference_card_state_${aid}_saved_${i}`}>
        <div style={{ display: "flex", gap: 12, justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>📁 {s.folder}</span>
            <span style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>Saved by {s.savedBy}</span>
            <span style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>{s.date}</span>
          </div>
          <EvidenceChip label="View source" />
        </div>
      </Card>
    ))}
  </div>
  );
};

/* 10. BRIEF */
const BriefTab = ({ co, aid, onExport }) => {
  const [showAdvancedExport, setShowAdvancedExport] = useState(false);
  const ALL_MODULES = ["Overview", "Payments + BNPL", "Signals", "Classification", "Hypotheses", "Buying Reality", "MEDDICC", "Contacts"];
  const [exportModules, setExportModules] = useState(() => {
    const init = {};
    ALL_MODULES.forEach(m => { init[m] = true; });
    return init;
  });
  return (
  <CardRow style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 16 }}>
    <div>
      <Card title="Brief Preview" tag="DRAFT" storageKey={`inference_card_state_${aid}_brief_preview`}>
        <div style={{
          background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 8,
          padding: "20px 24px", fontFamily: "'DM Sans', sans-serif",
        }}>
          <div style={{ borderBottom: `1px solid ${BORDER}`, paddingBottom: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 9, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginBottom: 8, letterSpacing: "0.08em" }}>INFERENCE BRIEF · {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }).toUpperCase()}</div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: TEXT_PRIMARY, letterSpacing: "-0.02em", marginBottom: 4 }}>{co.name}</h2>
            <div style={{ fontSize: 12, color: TEXT_MUTED }}>{co.industry} · {co.region} · Fit Score: <strong style={{ color: fitColor(co.fitScore) }}>{co.fitScore}/100</strong></div>
          </div>
          {[
            { title: "Primary Hypothesis", body: co.hypotheses.primary.text },
            { title: "Payments Stack", body: `${co.payments.providers.join(", ")} | BNPL: ${co.bnpl.providers.join(", ")} (${co.bnpl.positioning})` },
            ...(co.detectedPlatforms?.platforms?.length > 0 ? [{
              title: "Detected Platforms",
              body: co.detectedPlatforms.platforms
                .map(p => `${p.name} [${p.source === "fingerprint" ? "verified" : "inferred"}, ${p.confidence}%]${p.matched_target ? " ★ matches your ICP" : ""}`)
                .join(" · "),
            }] : []),
            { title: "Key Buying Triggers", body: co.buyingReality.triggers.slice(0, 2).join(" · ") },
            { title: "MEDDICC Gaps", body: `${co.meddicc.fields.filter(f => f.status === "unknown" || f.status === "gap").length} gaps identified across MEDDICC dimensions.` },
          ].map((s, i) => (
            <div key={i} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.06em", marginBottom: 4 }}>{s.title.toUpperCase()}</div>
              <p style={{ fontSize: 12, color: TEXT_MUTED, lineHeight: 1.65 }}>{s.body}</p>
            </div>
          ))}
        </div>
      </Card>
    </div>

    <div>
      <Card title="Export Options" collapsible={false}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {[
            { label: "Copy to Clipboard", icon: "M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3", format: "clipboard" },
            { label: "Export as PDF", icon: "M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z", format: "pdf" },
            { label: "Export as Markdown", icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z", format: "markdown" },
          ].map((e, i) => (
            <button key={i} data-testid={`export-${e.format}-btn`} onClick={() => onExport && onExport(e.format)} style={{
              display: "flex", alignItems: "center", gap: 10,
              background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 8,
              padding: "11px 14px", cursor: "pointer", transition: "all 0.15s",
              fontFamily: "'DM Sans', sans-serif",
            }}
              onMouseEnter={el => el.currentTarget.style.borderColor = "#374151"}
              onMouseLeave={el => el.currentTarget.style.borderColor = BORDER}
            >
              <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={TEXT_MUTED} strokeWidth={1.8}><path d={e.icon} /></svg>
              <span style={{ fontSize: 12, color: TEXT_PRIMARY }}>{e.label}</span>
            </button>
          ))}

          {/* Advanced export options — JSON moved here to declutter the main row */}
          <button
            data-testid="advanced-export-toggle"
            onClick={() => setShowAdvancedExport(s => !s)}
            style={{
              background: "transparent", border: "none", padding: "6px 2px",
              fontSize: 11, color: TEXT_DIM, cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif", textAlign: "left",
              alignSelf: "flex-start", textDecoration: "underline",
              textUnderlineOffset: 3,
            }}
            onMouseEnter={el => el.currentTarget.style.color = TEXT_MUTED}
            onMouseLeave={el => el.currentTarget.style.color = TEXT_DIM}
          >
            {showAdvancedExport ? "Hide advanced export options" : "Advanced export options"}
          </button>
          {showAdvancedExport && (
            <div data-testid="advanced-export-panel" style={{
              background: SURFACE_2, border: `1px dashed ${BORDER}`, borderRadius: 8,
              padding: "10px 12px", marginTop: 2,
            }}>
              <button
                data-testid="export-json-btn"
                onClick={() => onExport && onExport("json")}
                style={{
                  display: "flex", alignItems: "center", gap: 10, width: "100%",
                  background: "transparent", border: `1px solid ${BORDER}`, borderRadius: 6,
                  padding: "9px 12px", cursor: "pointer", transition: "all 0.15s",
                  fontFamily: "'DM Sans', sans-serif",
                }}
                onMouseEnter={el => el.currentTarget.style.borderColor = "#374151"}
                onMouseLeave={el => el.currentTarget.style.borderColor = BORDER}
              >
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={TEXT_MUTED} strokeWidth={1.8}>
                  <path d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
                <span style={{ fontSize: 12, color: TEXT_PRIMARY }}>Export as JSON</span>
              </button>
            </div>
          )}
        </div>
      </Card>

      <Card title="Included Modules">
        <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginBottom: 8, lineHeight: 1.5 }}>
          Click to toggle which sections are included in the export.
        </div>
        {["Overview", "Payments + BNPL", "Signals", "Classification", "Hypotheses", "Buying Reality", "MEDDICC", "Contacts"].map((m, i) => {
          const enabled = !!exportModules[m];
          return (
            <div
              key={i}
              data-testid={`brief-module-${m.toLowerCase().replace(/\s+\+\s+/g, "-").replace(/\s+/g, "-")}`}
              onClick={() => setExportModules(prev => ({ ...prev, [m]: !prev[m] }))}
              role="button"
              tabIndex={0}
              onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setExportModules(prev => ({ ...prev, [m]: !prev[m] })); } }}
              style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 4px", borderBottom: i < 7 ? `1px solid ${BORDER}` : "none", cursor: "pointer", userSelect: "none" }}
            >
              <div style={{
                width: 14, height: 14, borderRadius: 3,
                background: enabled ? `${ACCENT}22` : "transparent",
                border: `1px solid ${enabled ? ACCENT : BORDER}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.15s",
              }}>
                {enabled && <svg width={9} height={9} viewBox="0 0 24 24" fill="none" stroke={ACCENT} strokeWidth={2.5}><polyline points="20 6 9 17 4 12" /></svg>}
              </div>
              <span style={{ fontSize: 12, color: enabled ? TEXT_PRIMARY : TEXT_DIM }}>{m}</span>
            </div>
          );
        })}
      </Card>
    </div>
  </CardRow>
  );
};

/* ── MAP API DATA → COMPANY FORMAT ── */
function mapApiToCompany(data) {
  if (!data) return null;
  const identity = data.identity || {};
  const cls = data.classification || {};
  const pay = data.payments || {};
  const bnplData = data.bnpl || {};
  const detectedPlatformsRaw = data.detected_platforms || {};
  const fb = data.fit_breakdown || {};
  const traffic = data.traffic || {};
  const hyps = data.hypotheses || [];
  const meddiccRaw = data.meddicc || {};
  const arts = data.articles || [];
  const sigs = data.signals || [];
  const contacts = data.contacts || [];
  const icpDims = data.icp_dimension_scores || {};

  // Derive fit breakdown from ICP dimensions when components are all zero
  const allZero = !fb.icp_fit && !fb.persona_fit && !fb.payment_quality && !fb.signal_strength && !fb.identity_confidence;
  const icpFit = fb.icp_fit || (allZero ? (data.fit_score || 0) : 0);
  const personaFit = fb.persona_fit || (allZero && icpDims.persona_match ? (icpDims.persona_match.score || 0) : 0);
  const payQuality = fb.payment_quality || (allZero && pay.processors && pay.processors.length > 0 ? 70 : 0);
  const sigStrength = fb.signal_strength || (allZero ? Math.min(100, sigs.length * 8) : 0);
  const identConf = fb.identity_confidence || identity.confidence_score || (allZero && identity.legal_entity ? 70 : 0);

  return {
    name: data.company_name || "Unknown",
    legalName: identity.legal_entity || data.company_name || "Unknown",
    domain: data.domain || "N/A",
    industry: cls.vertical || "N/A",
    region: identity.headquarters || cls.region_inference || "N/A",
    operatingCountries: Array.isArray(identity.operating_countries) ? identity.operating_countries.filter(Boolean) : [],
    tier: data.fit_score >= 75 ? 1 : data.fit_score >= 45 ? 2 : 3,
    revenue: cls.revenue_tier || "N/A",
    employees: identity.employee_count || "N/A",
    bizModel: cls.business_model || "N/A",
    salesMotion: cls.gtm_motion || "N/A",
    fitScore: data.fit_score || 0,
    personaFit: personaFit,
    combinedFit: Math.round(((data.fit_score || 0) + personaFit) / 2),
    identityConf: identConf,
    modelVersion: "2.4",
    lastScan: data.completed_at ? new Date(data.completed_at).toLocaleString() : "Just now",
    payments: {
      providers: (pay.processors || []).map(_extractPayItemLabel).filter(Boolean),
      wallets: (pay.digital_wallets || []).map(_extractPayItemLabel).filter(Boolean),
      checkoutType: pay.checkout_quality || "N/A",
      confidence: payQuality || 80,
      evidence: [
        ...(Array.isArray(pay.evidence) ? pay.evidence : []),
        ..._extractPayItemEvidence(pay.processors),
        ..._extractPayItemEvidence(pay.digital_wallets),
      ],
    },
    bnpl: {
      offered: bnplData.detected || false,
      providers: (bnplData.products || []).map(_extractPayItemLabel).filter(Boolean),
      positioning: bnplData.detected ? (bnplData.positioning || "Detected") : "Not detected",
      confidence: bnplData.confidence || 80,
    },
    detectedPlatforms: {
      platforms: Array.isArray(detectedPlatformsRaw.platforms) ? detectedPlatformsRaw.platforms : [],
      fingerprintStatus: detectedPlatformsRaw.fingerprint_status || "failed",
      fingerprintReason: detectedPlatformsRaw.fingerprint_reason || null,
      detectionConfidence: detectedPlatformsRaw.detection_confidence || 0,
    },
    fitBreakdown: [
      { signal: "ICP Fit", weight: icpFit },
      { signal: "Persona Fit", weight: personaFit },
      { signal: "Payment Quality", weight: payQuality },
      { signal: "Signal Strength", weight: sigStrength },
      { signal: "Identity Confidence", weight: identConf },
    ],
    signals: sigs.map(s => ({
      type: s.type || "signal",
      label: s.description || "",
      value: s.description || "",
      confidence: s.strength === "strong" ? 90 : s.strength === "moderate" ? 70 : 50,
      sources: s.source ? [s.source] : [],
      is_missing: false,
    })),
    classification: {
      company_type: cls.customer_type || "N/A",
      business_model: cls.business_model || "N/A",
      product_category: cls.product_category || cls.vertical || "N/A",
      region_inference: cls.region_inference || identity.headquarters || "N/A",
      sales_motion: cls.gtm_motion || "N/A",
      confidence: cls.confidence || 85,
      evidence: cls.evidence || [],
      // Backend now emits {label, rationale, confidence}. Legacy shape used
      // {text, rationale}. Read both so old analyses don't blank out.
      alternates: (cls.alternates || []).map(a => ({
        label: a.label || a.text || "",
        reason: a.rationale || a.reason || "",
        confidence: a.confidence || 0,
      })),
    },
    complexity: {
      // Backend now emits top-level `decision_complexity` from the Operator
      // persona. Falls back to legacy nested shape and finally to a safe
      // default so the card is never blank on old data.
      score: data.decision_complexity?.score ?? 0,
      drivers: data.decision_complexity?.drivers?.length
        ? data.decision_complexity.drivers
        : ["Multi-stakeholder decision", "Integration complexity"],
      evidence: Array.isArray(data.decision_complexity?.evidence)
        ? data.decision_complexity.evidence.filter(Boolean)
        : (data.decision_complexity?.evidence ? [data.decision_complexity.evidence] : []),
    },
    hypotheses: {
      primary: hyps[0] ? {
        text: hyps[0].title || "",
        confidence: hyps[0].confidence || 0,
        evidence: hyps[0].evidence || [],
      } : { text: "No hypotheses generated", confidence: 0, evidence: [] },
      alternates: hyps.slice(1).map(h => ({ text: h.title || "", confidence: h.confidence || 0 })),
      // Prefer the top-level explicit_unknowns block (rich {question, why}
      // objects). Fall back to primary_hypothesis.constraints for legacy
      // analyses so older records don't blank out.
      unknowns: Array.isArray(data.explicit_unknowns) && data.explicit_unknowns.length
        ? data.explicit_unknowns.map(u =>
            typeof u === 'object' && u !== null ? (u.question || u.unknown || "") : String(u)
          ).filter(Boolean)
        : (hyps[0]?.constraints || []).map(c =>
            typeof c === 'object' && c !== null ? (c.discovery_question || c.what_is_missing || c.unknown || JSON.stringify(c)) : String(c)
          ).filter(Boolean),
    },
    discoveryQuestions: data.discovery_questions || null,
    approachRecommendations: data.approach_recommendations || null,
    // Key Contacts (Exa Call 7 — people search). Surfaced in the Buying
    // Reality tab. icpHasPersonas drives the empty-state ICP nudge.
    keyContacts: Array.isArray(data.key_contacts) ? data.key_contacts : [],
    icpHasPersonas: Array.isArray(data.icp_snapshot?.target_personas)
      ? (data.icp_snapshot.target_personas.length > 0)
      : Array.isArray(data.icpSnapshot?.target_personas)
        ? (data.icpSnapshot.target_personas.length > 0)
        : false,
    buyingReality: data.buying_reality ? {
      departments: data.buying_reality.departments || [],
      roles: (data.buying_reality.roles || []).map(r => ({
        role: r.role || "Unknown",
        identified: !!(r.name && r.name !== "Not identified"),
        name: r.name || "",
        note: [r.title, r.note].filter(Boolean).join(" — "),
      })),
      triggers: data.buying_reality.triggers || [],
      objections: (data.buying_reality.objections || []).map(o =>
        typeof o === 'object' && o !== null ? `${o.text || ""}${o.risk ? ` (${o.risk} risk)` : ""}` : String(o)
      ),
    } : {
      departments: [...new Set(contacts.map(c => c.role_type || "Unknown"))],
      roles: contacts.map(c => ({
        role: c.role_type || "Unknown",
        identified: true,
        name: c.name || "",
        note: c.title || "",
      })),
      triggers: hyps[0]?.pain_points || [],
      objections: [],
    },
    meddicc: {
      fields: Object.entries(meddiccRaw).map(([key, val]) => {
        const labelMap = { metrics: "Metrics", economic_buyer: "Economic Buyer", decision_criteria: "Decision Criteria", decision_process: "Decision Process", identify_pain: "Identify Pain", champion: "Champion", competition: "Competition" };
        const keyMap = { metrics: "M", economic_buyer: "E", decision_criteria: "D", decision_process: "D", identify_pain: "I", champion: "C", competition: "C" };
        // Backend produces status="filled"/"gap"/"unknown" with confidence,
        // recommended_question (NOT next_question), gap_description (NOT
        // gap). Previous mapping silently dropped both because of the field
        // name mismatch — these are the fields the user said were stale.
        const status = (val?.status || "unknown").toLowerCase();
        const fallbackConf = status === "filled" || status === "known" ? 80 : status === "gap" || status === "partial" ? 50 : 0;
        return {
          key: keyMap[key] || key[0]?.toUpperCase(),
          fullLabel: labelMap[key] || key,
          status,
          confidence: val?.confidence ?? fallbackConf,
          value: val?.value || null,
          evidence: Array.isArray(val?.evidence) ? val.evidence : (val?.evidence ? [val.evidence] : []),
          gap: val?.gap_description || val?.gap || (status === "unknown" ? `${labelMap[key] || key} not yet identified` : null),
          nextQuestion: val?.recommended_question || val?.next_question || null,
        };
      }),
    },
    traffic: {
      visits: traffic.monthly_visits || "N/A",
      trend: traffic.trend || "N/A",
      trendDirection: traffic.trend_direction || "unknown",
      trendReason: traffic.trend_reason || "",
      estimated: traffic.estimated !== undefined ? traffic.estimated : !(traffic.trend_series || []).length,
      primaryChannel: traffic.primary_channel || "N/A",
      source: traffic.source || "AI Analysis",
      confidence: traffic.confidence || 70,
      trend_series: (traffic.trend_series || traffic.trend_chart || []).map(t => ({
        period: t.period || t.month || "",
        value: t.value || t.visits || 0,
      })).filter(t => t.period && t.value > 0),
    },
    articles: arts.map(a => {
      let publisher = a.publisher || "Unknown";
      if (!a.publisher && a.source_url) {
        try {
          publisher = new URL(a.source_url).hostname.replace(/^www\./, "");
        } catch (e) {
          publisher = a.source_url.split('/')[0] || "Unknown";
        }
      }
      return {
        title: a.headline || a.title || "",
        publisher,
        published: a.date || "",
        relevance: a.relevance_score || 80,
        paywall: !!a.paywall,
        category: a.category || "",
        sourceUrl: a.source_url || "",
        snippet: a.snippet || a.relevance || "",
      };
    }),
    savedSummaries: data.saved_summaries || null,
    icpRefinementSuggestions: data.icp_refinement_suggestions || null,
    brief: data.brief || null,
    averageOrderValue: data.average_order_value || null,
    icpDimensionScores: data.icp_dimension_scores || {},
    dataQualityNotices: data.data_quality_notices || [],
  };
}


/* 11. ICP MATCH REPORT */
const ICPMatchTab = ({ co, icpConfig, aid, analysis, onReanalyze, isReanalyzing, workspaceId, onIcpChanged }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  useEffect(() => {
    const fetchSuggestions = async () => {
      setLoadingSuggestions(true);
      try {
        const res = await icpAPI.suggestions();
        if (res.data?.suggestions) setSuggestions(res.data.suggestions);
      } catch (err) { /* no suggestions available */ }
      setLoadingSuggestions(false);
    };
    fetchSuggestions();
  }, []);

  if (!icpConfig) {
    return (
      <Card title="ICP Match Report">
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <svg width={36} height={36} viewBox="0 0 24 24" fill="none" stroke={TEXT_DIM} strokeWidth={1.5} style={{ margin: "0 auto 16px" }}>
            <path d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
          </svg>
          <div style={{ fontSize: 14, fontWeight: 600, color: TEXT_PRIMARY, marginBottom: 6 }}>No ICP configured</div>
          <div style={{ fontSize: 12, color: TEXT_MUTED }}>Set up your Ideal Customer Profile in Settings to see how this company matches your target criteria.</div>
        </div>
      </Card>
    );
  }

  const icpFitScore = co.fitBreakdown?.find(f => f.signal === "ICP Fit")?.weight || 0;
  const personaFitScore = co.fitBreakdown?.find(f => f.signal === "Persona Fit")?.weight || 0;
  const aiScores = co.icpDimensionScores || {};
  const hasAiScores = Object.keys(aiScores).length > 0;

  const dimensions = [
    { label: "Industry Alignment", key: "industry_alignment", target: (icpConfig.target_industries || []).join(", ") || "Not specified", actual: co.industry || "Unknown" },
    { label: "Company Size", key: "company_size", target: icpConfig.company_size_min || icpConfig.company_size_max ? `${icpConfig.company_size_min || 'any'} - ${icpConfig.company_size_max || 'any'} employees` : "Not specified", actual: co.employees || "Unknown" },
    { label: "Revenue Range", key: "revenue_range", target: icpConfig.revenue_range || "Not specified", actual: co.revenue || "Unknown" },
    { label: "Region / Geography", key: "region", target: (icpConfig.target_regions || []).join(", ") || "Not specified", actual: co.region || "Unknown" },
    { label: "Business Model", key: "business_model", target: (icpConfig.business_models || []).join(", ") || "Not specified", actual: co.bizModel || "Unknown" },
    { label: "Target Platforms", key: "tech_stack", target: (icpConfig.target_platforms || []).join(", ") || (icpConfig.tech_stack || []).join(", ") || "Not specified", actual: co.detectedPlatforms?.platforms?.length > 0 ? "platforms" : (co.payments?.providers?.join(", ") || "None detected"), platformChips: co.detectedPlatforms?.platforms || [] },
    { label: "Target Personas", key: "persona_match", target: (icpConfig.target_personas || []).map(p => typeof p === 'object' ? `${p.title || ''} (${p.department || ''})` : p).join(", ") || "Not specified", actual: co.buyingReality?.roles?.map(r => r.role).join(", ") || "Unknown" },
    { label: "Average Order Value", key: "aov_match", target: icpConfig.average_order_value || "Not specified", actual: co.averageOrderValue?.estimated_aov || "Not detected" },
    { label: "Pain Points", key: "pain_point_alignment", target: (icpConfig.pain_points || []).join(", ") || "Not specified", actual: co.hypotheses?.primary?.text?.substring(0, 80) || "See hypotheses" },
    { label: "Use Cases", key: "use_case_alignment", target: (icpConfig.use_cases || []).join(", ") || "Not specified", actual: co.signals?.slice(0, 3).map(s => s.signal).join(", ") || "See signals" },
  ].map(d => ({
    ...d,
    match: aiScores[d.key]?.score ?? 50,
    rationale: aiScores[d.key]?.rationale || null,
  }));

  const overallMatch = icpFitScore || Math.round(dimensions.reduce((sum, d) => sum + d.match, 0) / dimensions.length);
  const matchColor = overallMatch >= 75 ? "#22C55E" : overallMatch >= 50 ? "#F59E0B" : "#EF4444";
  const aovData = co.averageOrderValue;
  const typeColors = { refine: ACCENT, add: "#22C55E", narrow: "#F59E0B", expand: "#8B5CF6", info: TEXT_DIM };

  // ── Stale-ICP detection: ICP updated after this analysis was scored ──
  const analysisIcpVer = analysis?.icp_version_at_run;
  const currentIcpVer = icpConfig?.updated_at;
  const isMarkedStale = !!analysis?.icp_stale;
  const versionStale = !!(analysisIcpVer && currentIcpVer && new Date(currentIcpVer) > new Date(analysisIcpVer));
  const showStaleBanner = isMarkedStale || versionStale;

  // ── Country coverage: which of the user's target countries this company
  // actually operates in. Hidden entirely when the brief has no country
  // evidence (older briefs predate the operating_countries field).
  const operating = co.operatingCountries || [];
  const targetCountries = icpConfig.target_countries || [];
  const operatingLower = operating.map(c => c.toLowerCase());
  const matchedTargets = targetCountries.filter(c => operatingLower.includes(c.toLowerCase()));
  const missedTargets = targetCountries.filter(c => !operatingLower.includes(c.toLowerCase()));
  const extraCountries = operating.filter(c => !targetCountries.some(t => t.toLowerCase() === c.toLowerCase()));

  return (
    <div data-testid="icp-match-tab" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, alignItems: "start" }}>
      <PresetFitComparison workspaceId={workspaceId} analysisId={aid} onPresetSwitched={onIcpChanged} />
      {showStaleBanner && (
        <div data-testid="icp-stale-banner" style={{
          gridColumn: "1 / -1",
          background: "linear-gradient(135deg, #2A1B05 0%, #1A1108 100%)",
          border: `1px solid #78350F`, borderRadius: 10, padding: "12px 16px",
          display: "flex", alignItems: "center", gap: 12,
          fontFamily: "'DM Sans', sans-serif",
        }}>
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#FCD34D", lineHeight: 1.4 }}>
              This analysis was scored against an older version of your ICP profile.
            </div>
            <div style={{ fontSize: 11, color: "#B45309", marginTop: 2, lineHeight: 1.4 }}>
              Scores and fit dot may not reflect your current criteria. Re-analyze to refresh against your updated ICP.
            </div>
          </div>
          <button data-testid="icp-stale-banner-cta" onClick={onReanalyze} disabled={isReanalyzing}
            style={{
              background: isReanalyzing ? "#78350F" : "#F59E0B",
              color: "#111", border: "none", borderRadius: 6,
              padding: "7px 14px", fontSize: 12, fontWeight: 700,
              cursor: isReanalyzing ? "wait" : "pointer",
              fontFamily: "'DM Sans', sans-serif", whiteSpace: "nowrap",
              transition: "background 0.15s", opacity: isReanalyzing ? 0.7 : 1,
            }}
            onMouseEnter={e => { if (!isReanalyzing) e.currentTarget.style.background = "#FBBF24"; }}
            onMouseLeave={e => { if (!isReanalyzing) e.currentTarget.style.background = "#F59E0B"; }}
          >{isReanalyzing ? "Re-analyzing..." : "Re-analyze"}</button>
        </div>
      )}
      {operating.length > 0 && (
        <Card
          testId="country-coverage-card"
          title="Country Coverage"
          tag={targetCountries.length > 0 ? `${matchedTargets.length}/${targetCountries.length} TARGETS` : `${operating.length} MARKETS`}
          storageKey={`inference_card_state_${aid}_country_coverage`}
          style={{ gridColumn: "1 / -1" }}
        >
          <div data-testid="country-coverage-chips" style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: targetCountries.length ? 12 : 0 }}>
            {matchedTargets.map(c => (
              <span key={`m-${c}`} data-testid={`country-chip-matched-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} style={{
                fontSize: 12, padding: "4px 11px", borderRadius: 16, display: "inline-flex", alignItems: "center", gap: 5,
                background: "#22C55E18", border: "1px solid #22C55E55", color: "#22C55E", fontWeight: 600,
              }}>
                <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}><path d="M20 6L9 17l-5-5" /></svg>
                {c}
              </span>
            ))}
            {extraCountries.map(c => (
              <span key={`e-${c}`} data-testid={`country-chip-other-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} style={{
                fontSize: 12, padding: "4px 11px", borderRadius: 16,
                background: SURFACE_2, border: `1px solid ${BORDER}`, color: TEXT_MUTED,
              }}>{c}</span>
            ))}
            {missedTargets.map(c => (
              <span key={`x-${c}`} data-testid={`country-chip-missing-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} style={{
                fontSize: 12, padding: "4px 11px", borderRadius: 16,
                background: "transparent", border: `1px dashed ${BORDER}`, color: TEXT_DIM,
                textDecoration: "line-through",
              }}>{c}</span>
            ))}
          </div>
          {targetCountries.length > 0 && (
            <p style={{ fontSize: 11.5, color: TEXT_MUTED, lineHeight: 1.6 }}>
              {matchedTargets.length > 0
                ? `Operates in ${matchedTargets.length} of your ${targetCountries.length} target ${targetCountries.length === 1 ? "country" : "countries"}. Struck-through countries are targets with no evidence of presence; grey chips are markets outside your target list.`
                : `No evidence of presence in your target countries. Grey chips show where this company does operate.`}
            </p>
          )}
          {targetCountries.length === 0 && (
            <p style={{ fontSize: 11.5, color: TEXT_MUTED, lineHeight: 1.6 }}>
              Add target countries to your ICP profile to see which of these markets match your focus.
            </p>
          )}
        </Card>
      )}

      <Card title="ICP Match Score" tag={hasAiScores ? "AI-SCORED" : "ESTIMATED"} collapsible={false}>        <div style={{ display: "flex", alignItems: "center", gap: 24, marginBottom: 20 }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 48, fontWeight: 800, color: matchColor, fontFamily: "'DM Sans', sans-serif", lineHeight: 1 }}>{overallMatch}</div>
            <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginTop: 4 }}>ICP FIT SCORE</div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, color: TEXT_PRIMARY, fontWeight: 600, marginBottom: 6 }}>
              {overallMatch >= 75 ? "Strong ICP Match" : overallMatch >= 50 ? "Moderate ICP Match" : "Weak ICP Match"}
            </div>
            <div style={{ fontSize: 12, color: TEXT_MUTED, lineHeight: 1.6 }}>
              {overallMatch >= 75
                ? `${co.name} closely aligns with your ideal customer profile across key dimensions.`
                : overallMatch >= 50
                ? `${co.name} partially matches your ICP. Some dimensions align well while others may need further investigation.`
                : `${co.name} has limited alignment with your defined ICP. Consider reviewing the dimension breakdown below.`}
            </div>
          </div>
        </div>
        <ConfBar value={overallMatch} color={matchColor} />
      </Card>

      <Card title="Match Summary" storageKey={`inference_card_state_${aid}_icp_summary`}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {[
            { label: "COMPANY FIT", value: co.fitScore, color: fitColor(co.fitScore) },
            { label: "ICP FIT", value: icpFitScore, color: fitColor(icpFitScore) },
            { label: "PERSONA FIT", value: personaFitScore, color: fitColor(personaFitScore) },
            { label: "DIMENSIONS MATCHED", value: `${dimensions.filter(d => d.match >= 70).length}/${dimensions.length}`, color: ACCENT },
          ].map((s, i) => (
            <div key={i} style={{ background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "12px 14px", textAlign: "center" }}>
              <div style={{ fontSize: 9, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginBottom: 6 }}>{s.label}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: s.color, fontFamily: "'DM Sans', sans-serif" }}>{s.value}</div>
            </div>
          ))}
        </div>
      </Card>

      {aovData && (
        <div style={{ gridColumn: "1 / -1" }}>
          <Card title="Average Order Value Analysis" tag={aovData.confidence ? `${aovData.confidence}% CONFIDENCE` : "DETECTED"} storageKey={`inference_card_state_${aid}_icp_aov`}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
              <div style={{ background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "16px", textAlign: "center" }}>
                <div style={{ fontSize: 9, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginBottom: 8 }}>ESTIMATED AOV</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: ACCENT, fontFamily: "'DM Sans', sans-serif" }}>{aovData.estimated_aov || "N/A"}</div>
                {aovData.currency && <div style={{ fontSize: 10, color: TEXT_DIM, marginTop: 4 }}>{aovData.currency}</div>}
              </div>
              <div style={{ background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "16px", textAlign: "center" }}>
                <div style={{ fontSize: 9, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginBottom: 8 }}>PRICING MODEL</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif" }}>{aovData.pricing_model || "N/A"}</div>
              </div>
              <div style={{ background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "16px", textAlign: "center" }}>
                <div style={{ fontSize: 9, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginBottom: 8 }}>YOUR TARGET AOV</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: icpConfig.average_order_value ? "#22C55E" : TEXT_MUTED, fontFamily: "'DM Sans', sans-serif" }}>{icpConfig.average_order_value || "Not set"}</div>
              </div>
            </div>
            {aovData.evidence && aovData.evidence.length > 0 && (
              <div style={{ marginTop: 14, padding: "10px 12px", background: `${ACCENT}08`, border: `1px solid ${ACCENT}22`, borderRadius: 6 }}>
                <div style={{ fontSize: 9, color: ACCENT, fontFamily: "'DM Mono', monospace", marginBottom: 6, letterSpacing: "0.06em" }}>EVIDENCE</div>
                {aovData.evidence.map((e, i) => (
                  <div key={i} style={{ fontSize: 11, color: TEXT_MUTED, lineHeight: 1.6, display: "flex", gap: 6 }}>
                    <span style={{ color: TEXT_DIM }}>-</span> {stripEvidencePrefix(e)}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      <div style={{ gridColumn: "1 / -1" }}>
        <Card title="Dimension-by-Dimension Analysis" tag={hasAiScores ? `${dimensions.length} AI-SCORED DIMENSIONS` : `${dimensions.length} DIMENSIONS`} storageKey={`inference_card_state_${aid}_icp_dimensions`}>
          {dimensions.map((d, i) => (
            <Card key={i} title={d.label} tag={`${d.match}%`} storageKey={`inference_card_state_${aid}_icp_dim_${d.key}`}
              titleAction={<span style={{ fontSize: 11, fontWeight: 700, color: confColor(d.match), fontFamily: "'DM Mono', monospace" }}>{d.match >= 70 ? "MATCH" : d.match >= 40 ? "PARTIAL" : "GAP"}</span>}>
              <ConfBar value={d.match} />
              {d.rationale && (
                <div style={{ fontSize: 11, color: TEXT_MUTED, fontStyle: "italic", marginTop: 6, lineHeight: 1.5, padding: "6px 10px", background: SURFACE_2, borderRadius: 4, borderLeft: `2px solid ${confColor(d.match)}` }}>
                  {d.rationale}
                </div>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 10 }}>
                <div>
                  <div style={{ fontSize: 9, color: ACCENT, fontFamily: "'DM Mono', monospace", marginBottom: 3, letterSpacing: "0.06em" }}>YOUR ICP TARGET</div>
                  <div style={{ fontSize: 11, color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif" }}>{d.target}</div>
                </div>
                <div>
                  <div style={{ fontSize: 9, color: "#22C55E", fontFamily: "'DM Mono', monospace", marginBottom: 3, letterSpacing: "0.06em" }}>COMPANY ACTUAL</div>
                  {d.platformChips && d.platformChips.length > 0 ? (
                    <div data-testid={`icp-dim-platforms-${d.key}`} style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 2 }}>
                      {d.platformChips.map(p => <PlatformChip key={`${p.name}-${p.source}`} platform={p} />)}
                    </div>
                  ) : (
                    <div style={{ fontSize: 11, color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif" }}>{d.actual}</div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </Card>
      </div>

      {(suggestions.length > 0 || loadingSuggestions) && (
        <div style={{ gridColumn: "1 / -1" }}>
          <Card title="ICP Refinement Suggestions" tag="AI-POWERED" storageKey={`inference_card_state_${aid}_icp_suggestions`}>
            {loadingSuggestions ? (
              <div style={{ textAlign: "center", padding: "20px 0", color: TEXT_DIM, fontSize: 12 }}>Analyzing your portfolio for patterns...</div>
            ) : (
              suggestions.map((s, i) => (
                <div key={i} style={{ padding: "12px 0", borderBottom: i < suggestions.length - 1 ? `1px solid ${BORDER}` : "none", display: "flex", gap: 12 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                    background: `${typeColors[s.type] || TEXT_DIM}14`,
                    border: `1px solid ${typeColors[s.type] || TEXT_DIM}33`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <span style={{ fontSize: 9, fontWeight: 700, color: typeColors[s.type] || TEXT_DIM, fontFamily: "'DM Mono', monospace", textTransform: "uppercase" }}>
                      {s.type === "refine" ? "R" : s.type === "add" ? "+" : s.type === "narrow" ? "N" : s.type === "expand" ? "E" : "i"}
                    </span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY }}>{s.title}</span>
                      <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 10, background: `${typeColors[s.type] || TEXT_DIM}14`, color: typeColors[s.type] || TEXT_DIM, fontFamily: "'DM Mono', monospace", fontWeight: 600, textTransform: "uppercase" }}>{s.dimension}</span>
                      {s.confidence && <span style={{ fontSize: 9, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>{s.confidence}% conf</span>}
                    </div>
                    <div style={{ fontSize: 12, color: TEXT_MUTED, lineHeight: 1.5 }}>{s.description}</div>
                  </div>
                </div>
              ))
            )}
          </Card>
        </div>
      )}
    </div>
  );
};

const STATIC_TABS = [
  { id: "overview", label: "Overview" },
  { id: "signals", label: "Signals" },
  { id: "classification", label: "Classification" },
  { id: "hypotheses", label: "Hypotheses" },
  { id: "buyingreality", label: "Buying Reality" },
  { id: "meddicc", label: "MEDDICC" },
  { id: "traffic", label: "Traffic", conditional: true },
  { id: "articles", label: "Articles" },
  { id: "summaries", label: "Saved Summaries" },
  { id: "icpmatch", label: "ICP Match" },
  { id: "brief", label: "Brief" },
];

/**
 * Build the live tab list with badges computed from the actual analysis
 * payload. Previously every badge was hardcoded ("9", "NEW", "3 gaps", "3"),
 * leading to UI counts that never matched what was on screen.
 *   - Signals badge = number of non-empty signal cards we will actually render
 *     (matches the empty-card filter in SignalsTab, so the badge can never
 *     drift from the rendered count).
 *   - MEDDICC badge  = count of dimensions with status="gap" / "unknown" /
 *     empty value, suffixed with "gaps".
 *   - Articles badge = number of articles present.
 *   - ICP Match badge = removed entirely (previously hardcoded "NEW").
 */
const _signalHasContent = (s) => Boolean(
  s && ((s.description || "").trim() || (s.value || "").trim() || (s.label || "").trim())
);
const buildTabs = (co) => {
  if (!co) return STATIC_TABS;
  const signalsCount = (co.signals || []).filter(_signalHasContent).length;
  const meddiccGaps = (co.meddicc?.fields || []).filter(d => {
    if (!d) return false;
    const status = (d.status || "").toLowerCase();
    const value = (d.value || "").toString().trim();
    // A dimension only counts as a gap when the UI would render the
    // UnknownFlag placeholder for it — i.e. no value at all. Populated
    // "gap" statuses (persona surfaced a value with an active discovery
    // question) render as normal cards and must NOT inflate the badge.
    if (value) return false;
    if (status === "filled" || status === "known") return false;
    return true;
  }).length;
  const articlesCount = (co.articles || []).length;
  return STATIC_TABS.map(t => {
    if (t.id === "signals") return { ...t, badge: signalsCount ? String(signalsCount) : null };
    if (t.id === "meddicc") return { ...t, badge: meddiccGaps ? `${meddiccGaps} gap${meddiccGaps === 1 ? "" : "s"}` : null };
    if (t.id === "articles") return { ...t, badge: articlesCount ? String(articlesCount) : null };
    return t;
  });
};

/* ── ICP Incomplete Warning Banner ── */
const ICPIncompleteBanner = ({ onGoToSettings, onDismiss }) => (
  <div data-testid="icp-incomplete-banner" style={{
    background: "#2A1B05",
    borderBottom: `1px solid #78350F`,
    padding: "10px 28px",
    display: "flex", alignItems: "center", gap: 12,
    fontFamily: "'DM Sans', sans-serif",
  }}>
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#FCD34D", lineHeight: 1.4 }}>
        Your ICP profile is incomplete — results may be less personalized.
      </div>
      <div style={{ fontSize: 11, color: "#B45309", marginTop: 2, lineHeight: 1.4 }}>
        Add target industries and personas to unlock sharper Fit scoring, ICP Match, and Brief tailoring.
      </div>
    </div>
    <button data-testid="icp-banner-cta" onClick={onGoToSettings} style={{
      background: "#F59E0B", color: "#111", border: "none",
      borderRadius: 6, padding: "7px 14px", fontSize: 12, fontWeight: 700,
      cursor: "pointer", fontFamily: "'DM Sans', sans-serif", whiteSpace: "nowrap",
      transition: "background 0.15s",
    }}
      onMouseEnter={e => { e.currentTarget.style.background = "#FBBF24"; }}
      onMouseLeave={e => { e.currentTarget.style.background = "#F59E0B"; }}
    >Complete ICP</button>
    <button data-testid="icp-banner-dismiss" onClick={onDismiss} aria-label="Dismiss" style={{
      background: "transparent", color: "#B45309", border: "none",
      cursor: "pointer", padding: 4, display: "flex", alignItems: "center",
      transition: "color 0.15s",
    }}
      onMouseEnter={e => { e.currentTarget.style.color = "#FCD34D"; }}
      onMouseLeave={e => { e.currentTarget.style.color = "#B45309"; }}
    >
      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round">
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </button>
  </div>
);

/* ── ROOT ── */
export default function InferenceResults({ analysis, workspaceId, onAnalysisUpdate, onNavigate, savedIds, onSaveAnalysis }) {
  const { user } = useAuth();
  // Default landing tab — persona-driven. Falls back to "overview" when the
  // user has no persona set (skip or pre-existing account). All 11 tabs
  // remain visible — the user can switch to any tab on first click.
  const initialTab = PERSONA_DEFAULT_TAB[user?.onboarding_persona] || "overview";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [icpConfig, setIcpConfig] = useState(null);
  const [icpLoaded, setIcpLoaded] = useState(false);
  const [icpBannerDismissed, setIcpBannerDismissed] = useState(() => {
    try { return sessionStorage.getItem("inference_icp_banner_dismissed") === "1"; } catch { return false; }
  });
  const [isReanalyzing, setIsReanalyzing] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentAnalysis, setCurrentAnalysis] = useState(analysis);
  const [showReanalyzeModal, setShowReanalyzeModal] = useState(false);
  const [showScenarioPanel, setShowScenarioPanel] = useState(false);
  const [scenarioText, setScenarioText] = useState("");
  const [scenarioRunning, setScenarioRunning] = useState(false);
  const [scenarioResult, setScenarioResult] = useState(null);
  const [showPassModal, setShowPassModal] = useState(false);
  const [passReasons, setPassReasons] = useState([]);
  const [passFreeText, setPassFreeText] = useState("");
  const [passRevisitDays, setPassRevisitDays] = useState(null);
  const [passSaving, setPassSaving] = useState(false);
  const [isPassed, setIsPassed] = useState(analysis?.passed || false);

  useEffect(() => {
    setCurrentAnalysis(analysis);
  }, [analysis]);

  // Reset to the persona default tab whenever a new analysis opens. The
  // `analysis_id` change is the trigger — same analysis re-render does not
  // reset (user keeps their current tab). All 11 tabs remain accessible.
  const analysisId = analysis?.analysis_id || analysis?.id;
  useEffect(() => {
    if (!analysisId) return;
    const target = PERSONA_DEFAULT_TAB[user?.onboarding_persona] || "overview";
    setActiveTab(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisId]);

  // Global ESC key handler — closes whichever modal is currently open.
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key !== "Escape") return;
      if (showPassModal) {
        setShowPassModal(false);
        setPassReasons([]);
        setPassFreeText("");
        setPassRevisitDays(null);
      } else if (showReanalyzeModal) {
        setShowReanalyzeModal(false);
      }
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [showReanalyzeModal, showPassModal]);

  const loadIcpConfig = useCallback(async () => {
    try {
      const res = await icpAPI.get();
      if (res.data) setIcpConfig(res.data);
    } catch (err) { /* no ICP configured */ }
    setIcpLoaded(true);
  }, []);

  useEffect(() => { loadIcpConfig(); }, [loadIcpConfig]);

  const icpIncomplete = icpLoaded && (
    !icpConfig ||
    ((!icpConfig.target_industries || icpConfig.target_industries.length === 0) &&
     (!icpConfig.target_personas || icpConfig.target_personas.length === 0))
  );

  const handleIcpCTA = () => {
    try { sessionStorage.setItem("inference_settings_initial_section", "icp"); } catch (e) { /* ignore */ }
    if (onNavigate) onNavigate("settings");
  };

  const handleIcpDismiss = () => {
    try { sessionStorage.setItem("inference_icp_banner_dismissed", "1"); } catch (e) { /* ignore */ }
    setIcpBannerDismissed(true);
  };

  const handlePass = async () => {
    if (!workspaceId || !currentAnalysis?.analysis_id || passSaving) return;
    setPassSaving(true);
    try {
      await analysisAPI.passDecision(workspaceId, currentAnalysis.analysis_id, {
        company_name: co?.name || currentAnalysis.company_name || "",
        reasons: passReasons,
        free_text: passFreeText,
        revisit_days: passRevisitDays,
      });
      setIsPassed(true);
      setShowPassModal(false);
      setPassReasons([]);
      setPassFreeText("");
      setPassRevisitDays(null);
    } catch (err) {
      console.error("Pass decision failed:", err);
    }
    setPassSaving(false);
  };


  const handleReanalyze = async () => {
    if (!workspaceId || !currentAnalysis?.analysis_id || isReanalyzing) return;
    setIsReanalyzing(true);
    try {
      await analysisAPI.reanalyze(workspaceId, currentAnalysis.analysis_id);
      // Poll until done
      const pollInterval = setInterval(async () => {
        try {
          const pollRes = await analysisAPI.poll(workspaceId, currentAnalysis.analysis_id);
          if (pollRes.data?.status === "completed") {
            clearInterval(pollInterval);
            // Fetch the full updated analysis
            const updated = await analysisAPI.get(workspaceId, currentAnalysis.analysis_id);
            if (updated.data) {
              setCurrentAnalysis(updated.data);
              if (onAnalysisUpdate) onAnalysisUpdate(updated.data);
            }
            setIsReanalyzing(false);
          } else if (pollRes.data?.status === "failed") {
            clearInterval(pollInterval);
            setIsReanalyzing(false);
          }
        } catch (err) {
          clearInterval(pollInterval);
          setIsReanalyzing(false);
        }
      }, 3000);
    } catch (err) {
      console.error("Re-analyze failed:", err);
      setIsReanalyzing(false);
    }
  };

  const handleRefreshBrief = async () => {
    if (!workspaceId || !currentAnalysis?.analysis_id || isRefreshing) return;
    setIsRefreshing(true);
    try {
      const res = await analysisAPI.refresh(workspaceId, currentAnalysis.analysis_id);
      const { mode, message, fields_updated: updated = [] } = res.data || {};
      const refreshed = await analysisAPI.get(workspaceId, currentAnalysis.analysis_id);
      if (refreshed.data) {
        setCurrentAnalysis(refreshed.data);
        if (onAnalysisUpdate) onAnalysisUpdate(refreshed.data);
      }
      if (mode === "partial") toast.warning("Partial refresh", { description: message });
      else if (updated.length === 0) toast.info("Already up to date", { description: message });
      else toast.success("Brief refreshed", { description: message });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refresh failed");
    }
    setIsRefreshing(false);
  };

  const handleScenarioRerun = async () => {
    if (!scenarioText.trim() || !workspaceId || !currentAnalysis?.analysis_id || scenarioRunning) return;
    setScenarioRunning(true);
    try {
      const res = await analysisAPI.scenarioRerun(workspaceId, currentAnalysis.analysis_id, scenarioText.trim());
      const newAnalysisId = res.data?.analysis_id;
      if (!newAnalysisId) throw new Error("No analysis ID returned");

      // Poll the scenario analysis until done
      const pollInterval = setInterval(async () => {
        try {
          const pollRes = await analysisAPI.poll(workspaceId, newAnalysisId);
          if (pollRes.data?.status === "completed") {
            clearInterval(pollInterval);
            const updated = await analysisAPI.get(workspaceId, newAnalysisId);
            if (updated.data) {
              setScenarioResult(updated.data);
            }
            setScenarioRunning(false);
          } else if (pollRes.data?.status === "failed") {
            clearInterval(pollInterval);
            setScenarioRunning(false);
          }
        } catch (err) {
          clearInterval(pollInterval);
          setScenarioRunning(false);
        }
      }, 3000);
    } catch (err) {
      console.error("Scenario re-run failed:", err);
      setScenarioRunning(false);
    }
  };

  const co = useMemo(() => {
    if (currentAnalysis) {
      const mapped = mapApiToCompany(currentAnalysis);
      if (!mapped) return null;
      // Mark detected platforms that match the user's ICP target_platforms.
      const targets = (icpConfig?.target_platforms || []).map(s => (s || "").toLowerCase());
      if (targets.length > 0 && mapped.detectedPlatforms?.platforms) {
        mapped.detectedPlatforms.platforms = mapped.detectedPlatforms.platforms.map(p => ({
          ...p,
          matched_target: targets.includes((p.name || "").toLowerCase()),
        }));
      }
      return mapped;
    }
    return null;
  }, [currentAnalysis, icpConfig]);

  const handleExport = async (format) => {
    if (!workspaceId || !analysis?.analysis_id) return;
    try {
      // 1. Create export record on backend (this builds + persists content)
      const createRes = await exportAPI.create({
        workspace_id: workspaceId,
        analysis_id: analysis.analysis_id,
        format: format,
      });
      const exportId = createRes?.data?.export_id;
      if (!exportId) throw new Error("No export_id returned");

      // 2. Fetch the rendered content (the create response strips it to
      // keep the list endpoint light; the get endpoint returns it in full).
      const contentRes = await exportAPI.get(exportId);
      const content = contentRes?.data?.content;
      if (!content) throw new Error("Export content not returned");

      // 3. Trigger browser download. PDF content is stored as hex-encoded
      // bytes by the backend when fpdf2 generated a real PDF; decode to a
      // Uint8Array + set the correct MIME so browsers save a proper .pdf
      // file. Markdown/JSON/CSV are stored as UTF-8 text.
      const companyName = (co?.name || analysis.company_name || "analysis").replace(/[^a-z0-9-]/gi, "_").toLowerCase();
      const isRealPdf = format === "pdf" && /^[0-9a-f]+$/i.test(content) && content.length % 2 === 0;
      const ext = format === "json" ? "json" : format === "csv" ? "csv" : (format === "pdf" && isRealPdf) ? "pdf" : "md";
      const mime = format === "json" ? "application/json" : format === "pdf" ? "application/pdf" : "text/plain;charset=utf-8";
      let blob;
      if (isRealPdf) {
        // Hex-encoded PDF bytes — decode to Uint8Array.
        const bytes = new Uint8Array(content.length / 2);
        for (let i = 0; i < bytes.length; i++) bytes[i] = parseInt(content.substr(i * 2, 2), 16);
        blob = new Blob([bytes], { type: mime });
      } else {
        // Backend fell back to markdown (e.g. fpdf2 failure); download as .md
        blob = new Blob([content], { type: format === "pdf" ? "text/plain;charset=utf-8" : mime });
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${companyName}-${analysis.analysis_id}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch (err) {
      console.error('Export failed:', err);
      // Surface to the user — silent failure was the original bug.
      try { window.alert(`Export failed: ${err?.response?.data?.detail || err.message || "unknown error"}`); } catch (e) { /* ignore */ }
    }
  };

  const aid = currentAnalysis?.analysis_id || "";

  const renderTab = () => {
    switch (activeTab) {
      case "overview": return <><DataQualityNotice notices={co.dataQualityNotices} tab="overview" /><OverviewTab co={co} aid={aid} /></>;
      case "signals": return <><DataQualityNotice notices={co.dataQualityNotices} tab="signals" /><SignalsTab co={co} aid={aid} /></>;
      case "classification": return <><DataQualityNotice notices={co.dataQualityNotices} tab="classification" /><ClassificationTab co={co} aid={aid} /></>;
      case "hypotheses": return <><DataQualityNotice notices={co.dataQualityNotices} tab="hypotheses" /><HypothesesTab co={co} aid={aid} /></>;
      case "buyingreality": return <><DataQualityNotice notices={co.dataQualityNotices} tab="buying_reality" /><BuyingRealityTab co={co} aid={aid} /></>;
      case "meddicc": return <><DataQualityNotice notices={co.dataQualityNotices} tab="meddicc" /><MeddiccTab co={co} aid={aid} /></>;
      case "traffic": return <><DataQualityNotice notices={co.dataQualityNotices} tab="traffic" /><TrafficTab co={co} aid={aid} /></>;
      case "articles": return <><DataQualityNotice notices={co.dataQualityNotices} tab="articles" /><ArticlesTab co={co} aid={aid} /></>;
      case "summaries": return <SavedSummariesTab co={co} aid={aid} />;
      case "icpmatch": return <ICPMatchTab co={co} icpConfig={icpConfig} aid={aid} analysis={currentAnalysis} onReanalyze={handleReanalyze} isReanalyzing={isReanalyzing} workspaceId={workspaceId} onIcpChanged={loadIcpConfig} />;
      case "brief": return <BriefTab co={co} aid={aid} onExport={handleExport} />;
      default: return null;
    }
  };

  if (!co) {
    return (
      <div data-testid="analysis-results-page" style={{ fontFamily: "'DM Sans', sans-serif", background: BG, color: TEXT_PRIMARY, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", maxWidth: 400 }}>
          <svg width={40} height={40} viewBox="0 0 24 24" fill="none" stroke={TEXT_DIM} strokeWidth={1.5} style={{ margin: "0 auto 16px" }}>
            <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>No analysis data available</div>
          <div style={{ fontSize: 13, color: TEXT_MUTED }}>This analysis hasn't been processed yet or the data is unavailable. Click Re-analyze to generate results.</div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="analysis-results-page" style={{ fontFamily: "'DM Sans', sans-serif", background: BG, color: TEXT_PRIMARY, minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-thumb { background: #1E2433; border-radius: 4px; }
        button { outline: none; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.7; } }
      `}</style>

      <CompanyHeader company={co} aid={aid} activeTab={activeTab} onTabChange={setActiveTab} tabs={buildTabs(co)}
        onReanalyze={handleReanalyze} onScenarioRerun={() => setShowScenarioPanel(true)}
        onPassClick={() => setShowPassModal(true)}
        onSaveClick={() => {
          const id = analysis?.analysis_id || analysis?.id;
          if (id && onSaveAnalysis) onSaveAnalysis(id);
        }}
        onExportClick={() => handleExport("pdf")}
        isSaved={savedIds && (savedIds.has?.(analysis?.analysis_id) || savedIds.has?.(analysis?.id))}
        isReanalyzing={isReanalyzing} showReanalyzeModal={showReanalyzeModal} setShowReanalyzeModal={setShowReanalyzeModal}
        onRefresh={handleRefreshBrief} isRefreshing={isRefreshing} />

      {icpIncomplete && !icpBannerDismissed && (
        <ICPIncompleteBanner onGoToSettings={handleIcpCTA} onDismiss={handleIcpDismiss} />
      )}

      <main style={{ flex: 1, padding: "24px 28px" }}>
        {renderTab()}
      </main>

      {/* Part 18 — Scenario Re-run Slide-over Panel */}
      {showScenarioPanel && (
        <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: 420, background: SURFACE,
          borderLeft: `1px solid ${BORDER}`, zIndex: 100, display: "flex", flexDirection: "column",
          boxShadow: "-8px 0 32px rgba(0,0,0,0.4)", animation: "slideIn 0.2s ease" }}>
          <style>{`@keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
          <div style={{ padding: "20px 24px", borderBottom: `1px solid ${BORDER}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: TEXT_PRIMARY }}>Scenario Re-run</div>
              <div style={{ fontSize: 11, color: TEXT_MUTED, marginTop: 2 }}>Describe what changed or what you want to explore</div>
            </div>
            <button data-testid="close-scenario-panel" onClick={() => { setShowScenarioPanel(false); setScenarioResult(null); setScenarioText(""); }}
              style={{ background: "none", border: "none", color: TEXT_DIM, cursor: "pointer", fontSize: 18 }}>x</button>
          </div>

          <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
            <textarea data-testid="scenario-text-input" value={scenarioText} onChange={e => setScenarioText(e.target.value.slice(0, 500))}
              placeholder="e.g. They just raised a Series B, the CEO resigned, they acquired a competitor..."
              style={{
                width: "100%", minHeight: 100, background: SURFACE_2, border: `1px solid ${BORDER}`,
                borderRadius: 8, padding: 12, fontSize: 13, color: TEXT_PRIMARY, resize: "vertical",
                fontFamily: "'DM Sans', sans-serif", outline: "none",
              }} />
            <div style={{ fontSize: 10, color: TEXT_DIM, marginTop: 4, textAlign: "right" }}>{scenarioText.length}/500</div>

            {/* Quick suggestions */}
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginBottom: 8, letterSpacing: "0.06em" }}>QUICK SUGGESTIONS</div>
              {["What if they raised a Series B?", "What if the CEO resigned?", "What if they acquired a competitor?", "What if they announced layoffs?"].map(s => (
                <button key={s} data-testid={`suggestion-${s.slice(0,20).replace(/\s/g,'-')}`}
                  onClick={() => setScenarioText(s)}
                  style={{
                    display: "block", width: "100%", textAlign: "left", background: SURFACE_2,
                    border: `1px solid ${BORDER}`, borderRadius: 6, padding: "8px 12px", marginBottom: 6,
                    fontSize: 12, color: TEXT_MUTED, cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={e => { e.target.style.borderColor = ACCENT; e.target.style.color = TEXT_PRIMARY; }}
                  onMouseLeave={e => { e.target.style.borderColor = BORDER; e.target.style.color = TEXT_MUTED; }}
                >{s}</button>
              ))}
            </div>

            <button data-testid="run-scenario-btn" onClick={handleScenarioRerun} disabled={!scenarioText.trim() || scenarioRunning}
              style={{
                width: "100%", marginTop: 20, padding: "12px 0", borderRadius: 8,
                background: scenarioRunning ? `${ACCENT}44` : ACCENT, color: "#fff",
                border: "none", fontSize: 13, fontWeight: 600, cursor: scenarioRunning ? "wait" : "pointer",
                fontFamily: "'DM Sans', sans-serif", opacity: !scenarioText.trim() ? 0.4 : 1,
              }}>{scenarioRunning ? "Running scenario..." : "Run Scenario"}</button>

            {/* Scenario Result Delta */}
            {scenarioResult && (() => {
              const scenarioCo = mapApiToCompany(scenarioResult);
              if (!scenarioCo) return null;
              const origFit = co.fitScore;
              const newFit = scenarioCo.fitScore;
              const fitChanged = origFit !== newFit;
              const origSignals = co.signals?.length || 0;
              const newSignals = scenarioCo.signals?.length || 0;
              return (
                <div style={{ marginTop: 20, borderTop: `1px solid ${BORDER}`, paddingTop: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: ACCENT, marginBottom: 12, fontFamily: "'DM Mono', monospace" }}>WHAT CHANGED</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {fitChanged && (
                      <div style={{ background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 6, padding: "10px 12px" }}>
                        <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginBottom: 4 }}>FIT SCORE</div>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <span style={{ fontSize: 16, fontWeight: 700, color: fitColor(origFit) }}>{origFit}</span>
                          <span style={{ color: TEXT_DIM }}>→</span>
                          <span style={{ fontSize: 16, fontWeight: 700, color: fitColor(newFit) }}>{newFit}</span>
                          <span style={{ fontSize: 11, color: newFit > origFit ? "#22C55E" : "#EF4444", fontWeight: 600 }}>
                            ({newFit > origFit ? "+" : ""}{newFit - origFit})
                          </span>
                        </div>
                      </div>
                    )}
                    {origSignals !== newSignals && (
                      <div style={{ background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 6, padding: "10px 12px" }}>
                        <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginBottom: 4 }}>SIGNALS</div>
                        <span style={{ fontSize: 13, color: TEXT_PRIMARY }}>{origSignals} → {newSignals} signals</span>
                      </div>
                    )}
                    {scenarioCo.hypotheses?.primary?.text && scenarioCo.hypotheses.primary.text !== co.hypotheses?.primary?.text && (
                      <div style={{ background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 6, padding: "10px 12px" }}>
                        <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginBottom: 4 }}>PRIMARY HYPOTHESIS CHANGED</div>
                        <div style={{ fontSize: 12, color: TEXT_PRIMARY, lineHeight: 1.5 }}>{scenarioCo.hypotheses.primary.text.slice(0, 200)}...</div>
                      </div>
                    )}
                    <button data-testid="view-scenario-results"
                      onClick={() => { setCurrentAnalysis(scenarioResult); setShowScenarioPanel(false); setScenarioResult(null); setScenarioText(""); }}
                      style={{
                        width: "100%", marginTop: 8, padding: "10px 0", borderRadius: 8,
                        background: SURFACE_2, color: ACCENT, border: `1px solid ${ACCENT}`,
                        fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                      }}>View Full Scenario Results</button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Part 9.5/16 — Pass Decision Modal */}
      {showPassModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={e => { if (e.target === e.currentTarget) setShowPassModal(false); }}>
          <div data-testid="pass-modal" role="dialog" aria-modal="true" style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "28px 32px", width: 440, maxHeight: "80vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4, gap: 10 }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: TEXT_PRIMARY, margin: 0 }}>Why are you passing on {co?.name}?</h2>
              <button data-testid="pass-modal-close" onClick={() => { setShowPassModal(false); setPassReasons([]); setPassFreeText(""); setPassRevisitDays(null); }} aria-label="Close" style={{
                background: "transparent", color: TEXT_DIM, border: "none", cursor: "pointer",
                padding: 4, display: "flex", alignItems: "center", borderRadius: 4,
                transition: "color 0.15s", flexShrink: 0,
              }}
                onMouseEnter={e => { e.currentTarget.style.color = TEXT_PRIMARY; }}
                onMouseLeave={e => { e.currentTarget.style.color = TEXT_DIM; }}
              >
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <p style={{ fontSize: 12, color: TEXT_MUTED, marginBottom: 20 }}>Select one or more reasons. This helps your team learn from decisions.</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
              {["Poor ICP fit", "Budget not confirmed", "Wrong timing", "Champion not identified", "Competitor already entrenched", "Company financials concerning", "Other"].map(reason => (
                <button key={reason} data-testid={`pass-reason-${reason.replace(/\s/g, '-').toLowerCase()}`}
                  onClick={() => setPassReasons(prev => prev.includes(reason) ? prev.filter(r => r !== reason) : [...prev, reason])}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 8,
                    background: passReasons.includes(reason) ? `${ACCENT}14` : SURFACE_2, border: `1px solid ${passReasons.includes(reason) ? ACCENT : BORDER}`,
                    color: passReasons.includes(reason) ? ACCENT : TEXT_MUTED, cursor: "pointer", fontSize: 13, textAlign: "left", fontFamily: "'DM Sans', sans-serif" }}>
                  <div style={{ width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${passReasons.includes(reason) ? ACCENT : BORDER}`, background: passReasons.includes(reason) ? ACCENT : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {passReasons.includes(reason) && <span style={{ color: "#fff", fontSize: 10 }}>&#10003;</span>}
                  </div>
                  {reason}
                </button>
              ))}
            </div>
            {passReasons.includes("Wrong timing") && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginBottom: 6 }}>REVISIT IN</div>
                <div style={{ display: "flex", gap: 6 }}>
                  {[30, 60, 90, 180].map(d => (
                    <button key={d} data-testid={`revisit-${d}`} onClick={() => setPassRevisitDays(d)}
                      style={{ padding: "6px 12px", borderRadius: 6, fontSize: 12, cursor: "pointer",
                        background: passRevisitDays === d ? `${ACCENT}14` : SURFACE_2, border: `1px solid ${passRevisitDays === d ? ACCENT : BORDER}`,
                        color: passRevisitDays === d ? ACCENT : TEXT_MUTED, fontFamily: "'DM Sans', sans-serif" }}>{d} days</button>
                  ))}
                </div>
              </div>
            )}
            <textarea data-testid="pass-free-text" value={passFreeText} onChange={e => setPassFreeText(e.target.value)}
              placeholder="Additional notes (optional)..." style={{ width: "100%", minHeight: 60, background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 8, padding: 10, fontSize: 12, color: TEXT_PRIMARY, resize: "vertical", fontFamily: "'DM Sans', sans-serif", outline: "none", marginBottom: 16 }} />
            <div style={{ display: "flex", gap: 8 }}>
              <button data-testid="cancel-pass-btn" onClick={() => { setShowPassModal(false); setPassReasons([]); setPassFreeText(""); setPassRevisitDays(null); }}
                style={{ flex: 1, padding: "10px 0", borderRadius: 8, background: SURFACE_2, color: TEXT_MUTED, border: `1px solid ${BORDER}`, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
              <button data-testid="confirm-pass-btn" onClick={handlePass} disabled={passReasons.length === 0 || passSaving}
                style={{ flex: 1, padding: "10px 0", borderRadius: 8, background: passReasons.length > 0 ? "#EF4444" : SURFACE_2, color: "#fff", border: "none", fontSize: 13, fontWeight: 600, cursor: passReasons.length > 0 ? "pointer" : "not-allowed", opacity: passReasons.length > 0 ? 1 : 0.4 }}>
                {passSaving ? "Saving..." : "Confirm Pass"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
